module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chapter-title",
    attrs: {
      "id": "ch-1"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_c('span', {
    staticClass: "chap"
  }, [_c('strong', [_vm._v("1")])]), _vm._v(" The Debate Over Eschatology")])]), _c('a', {
    attrs: {
      "id": "page_3"
    }
  }), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_vm._v("The Kingdom of God has received such intensive study during the last few decades that a recent survey of New Testament research can speak of “the discovery of the true meaning of the Kingdom of God.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_1",
      "id": "foot-1"
    }
  }, [_vm._v("1")]), _vm._v(" There is a growing consensus in New Testament scholarship that the Kingdom of God is in some sense both present and future. However, while much progress has been achieved, the question of the role of apocalyptic concepts in our Lord’s teaching, and the relationship between the present and future aspects of the Kingdom continue to be vigorously debated. To place the present study in perspective, we must survey the background of the contemporary discussion, giving particular attention to the role of apocalyptic."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_2",
      "id": "foot-2"
    }
  }, [_vm._v("2")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The prevailing interpretation of fifty years ago minimized the significance of eschatological and apocalyptic concepts. Adolf von Harnack’s “old liberal” view treated the apocalyptic element in "), _c('a', {
    attrs: {
      "id": "page_4"
    }
  }, [_vm._v("Jesus’ teachings as the husk which contained the kernel of his real religious message. This consisted of a few universal truths such as the fatherhood of God, the infinite value of the individual soul, and the ethic of love."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_3",
      "id": "foot-3"
    }
  }, [_vm._v("3")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Conservative scholars like James Orr and A. B. Bruce understood the Kingdom of God to be a new principle—the reign of God—introduced into the world by our Lord and destined through the church to transform all areas of human society. They interpreted apocalyptic terminology symbolically of the divine activity in history. A similar interpretation has been recently defended by Roderick Campbell."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_4",
      "id": "foot-4"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec1"
  }, [_vm._v("T"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("HE")]), _vm._v(" E"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("SCHATOLOGICAL")]), _vm._v(" I"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("NTERPRETATION")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The work of Johannes Weiss and Albert Schweitzer marked a turning point in biblical criticism, for these scholars recognized that apocalyptic was fundamental in Jesus’ teaching and that the prevailing noneschatological views were modernizations rather than sound historical analyses. This view of Consistent Eschatology is too well known to be outlined here. Several observations are, however, in order. Schweitzer did not achieve his interpretation by an inductive study of the Gospels, but by assuming that Jesus must be interpreted in terms of his environment, which Schweitzer understood to be that of Jewish apocalyptic."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_5",
      "id": "foot-5"
    }
  }, [_vm._v("5")])]), _vm._v(" The result was a historical figure who belonged only to the first century. “The historical knowledge of the personality and life of Jesus will not be a help, but perhaps even an offence to religion.… Jesus as a concrete historical personality remains "), _c('a', {
    attrs: {
      "id": "page_5"
    }
  }, [_vm._v("a stranger to our time …,”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_6",
      "id": "foot-6"
    }
  }, [_vm._v("6")]), _vm._v(" for he was a deluded fanatic who futilely threw his life away in blind devotion to a mad apocalyptic dream which was never realized and which, in Schweitzer’s view, never could be realized.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Schweitzer’s interpretation involved three elements which must not be confused: ("), _c('em', [_vm._v("a")]), _vm._v(") Apocalyptic is an essential element in Jesus’ message of the Kingdom. ("), _c('em', [_vm._v("b")]), _vm._v(") Jesus’ message is exclusively eschatological. In no sense of the word could the Kingdom be interpreted as a present spiritual reality. It is the apocalyptic age to come. ("), _c('em', [_vm._v("c")]), _vm._v(") Jesus thought that the Kingdom would come at once in his lifetime. These three points must be kept in mind as we continue our survey of criticism, for one may agree with Schweitzer in part if not altogether.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1"
  }, [_vm._v("E"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("SCHATOLOGICAL")]), _vm._v(" I"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("NTERPRETATIONS SINCE")]), _vm._v(" S"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("CHWEITZER")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The history of criticism since Schweitzer may be described as a struggle over eschatology. Consistent Eschatology has found many supporters on the Continent, although often in a modified form. Wilhelm Michaelis in "), _c('em', [_vm._v("Täufer, Jesus, Urgemeinde")]), _vm._v(" (1928) argued that Jesus’ teachings must be interpreted consistently from a single point of view: viz., that the Kingdom of God was apocalyptic and imminent. The Kingdom did not come; in its stead came Easter and Pentecost. These acts of God make possible for the church a reinterpretation of the Kingdom as both a future and a present reality. In a later work, "), _c('em', [_vm._v("Der Herr verzieht nicht die Verheissung")]), _vm._v(" (1942), Michaelis reacted against Consistent Eschatology to the extent of insisting that Jesus’ emphasis was not upon the futurity and imminence of the Kingdom as such, but that his emphasis upon imminence "), _c('a', {
    attrs: {
      "id": "page_6"
    }
  }, [_vm._v("had the spiritual purpose of creating a response of watchfulness in the disciples.")])]), _c('a', {
    attrs: {
      "id": "page_6"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_6"
    }
  }, [_vm._v("Maurice Goguel’s "), _c('em', [_vm._v("La Vie de Jesus")]), _vm._v(" has been one of the most widely used texts in America for the life of Christ. Goguel is not usually classed as an adherent of Consistent Eschatology, for he attempts to distinguish between apocalyptic and eschatological elements in Jesus’ teachings and holds that eschatology was merely the framework of his thought."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_7",
      "id": "foot-7"
    }
  }, [_vm._v("7")]), _vm._v(" Nevertheless, Goguel attributed to Jesus an exclusively futuristic view of the Kingdom, although with changing emphasis. In his early preaching mission, Jesus thought the Kingdom was to occur at once (Matt. 10:23). Later, after the Galilean crisis, he expected it within a few years (Mark 9:1). Finally, Jesus concluded that only God knew the time of the end (Mark 13:32). Goguel sees no modification of the eschatological character of the hope even though the time of its coming receded in Jesus’ thought.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("However, Goguel holds that Jesus’ thought was eschatological and not apocalyptic. Eschatology looks forward to a future separation of men and the coming of a new order displacing the present world. Apocalyptic attempts to picture in advance the form which the cosmic drama will take and the succession of events which will accompany the transition. Therefore, apocalyptic tries to calculate the time of the end by the study of signs. Jesus dismissed this sort of apocalyptic speculation as well as all apocalyptic representations of the future (Luke 17:21)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_8",
      "id": "foot-8"
    }
  }, [_vm._v("8")])]), _vm._v(" In spite of this distinction, Goguel’s interpretation of Jesus makes him a teacher of a thoroughgoing futuristic eschatology, for his view of the Kingdom is exclusively futuristic and catastrophic. This raises the fundamental question of whether Goguel’s distinction between eschatology and apocalyptic is valid.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A second important life of Christ by a French scholar, Charles Guignebert, also espouses Consistent Eschatology. Guignebert rejects interpretations of the Kingdom as a present reality because the Kingdom “is primarily and essentially the material transformation "), _c('a', {
    attrs: {
      "id": "page_7"
    }
  }, [_vm._v("of this present evil world,” i.e., the eschatological salvation of the age to come."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_9",
      "id": "foot-9"
    }
  }, [_vm._v("9")]), _vm._v(" Jesus’ mission was to announce the imminent end of the age and the coming of the eschatological order.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Martin Werner has written a history of dogma which assumes Consistent Eschatology in the teachings of Jesus as its point of departure and interprets the development of early dogma in terms of the church’s adjustment to the failure of the Parousia to occur."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_10",
      "id": "foot-10"
    }
  }, [_vm._v("10")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A typical statement of critical German theology is that of Martin Dibelius in his "), _c('em', [_vm._v("Jesus")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_11",
      "id": "foot-11"
    }
  }, [_vm._v("11")])]), _vm._v(" The Kingdom is the eschatological act of God establishing his rule in the universe. Jesus taught that this divine act was about to occur; in fact, it was already in process. The tension between the future and the present is the tension between the Kingdom in complete fulfillment and the Kingdom in process of breaking in upon the present order. Thus the signs of the Kingdom are present, though not the Kingdom itself. The powers of the Kingdom are present, although the actual coming of the Kingdom awaits the apocalyptic act of God which must shortly occur. Foregleams of its splendor are already discernible in Jesus’ works."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_12",
      "id": "foot-12"
    }
  }, [_vm._v("12")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The most important contemporary support of Consistent Eschatology is found in the interpretation of Rudolf Bultmann and some of his followers. For Bultmann, Jesus was only a Jewish apocalyptic prophet announcing the imminent inbreaking of the Kingdom of God. He insists that any interpretation which sees the Kingdom as a present reality in Jesus’ person and in his followers is “escape-reasoning” designed to avoid the difficulty created by the failure of the announced imminent irruption of God’s reign to occur. The view of a present Kingdom “cannot be substantiated by a single saying of Jesus, and it contradicts the meaning of ‘God’s Reign.’ ”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_13",
      "id": "foot-13"
    }
  }, [_vm._v("13")])]), _vm._v(" Thus for "), _c('a', {
    attrs: {
      "id": "page_8"
    }
  }, [_vm._v("Bultmann, Jesus and his apocalyptic message are not a part of New Testament theology but belong to Judaism. The one difference between the message of Jesus and that of other Jewish apocalyptists is the certainty with which Jesus asserted the immediacy of the end. So strong is this certainty that Jesus viewed the future Kingdom as actually breaking in. The Kingdom is not present, but it is dawning."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_14",
      "id": "foot-14"
    }
  }, [_vm._v("14")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Through Bultmann’s influence, a number of scholars have concluded that Jesus’ main emphasis was not that of a future apocalyptic kingdom but of the "), _c('em', [_vm._v("immediate")]), _vm._v(" coming of this kingdom. Immediacy rather than eschatology became central. Erich Grässer assumed that this note of immediacy was the heart of Jesus’ proclamation, and the failure of the Parousia to occur at once was the great theological problem with which the early church had to contend."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_15",
      "id": "foot-15"
    }
  }, [_vm._v("15")])]), _vm._v(" Hans Conzelmann has attempted to show how Luke historicized the purely eschatological message of Jesus."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_16",
      "id": "foot-16"
    }
  }, [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In England, Consistent Eschatology has never taken deep root. F. C. Burkitt finally assented to the position;"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_17",
      "id": "foot-17"
    }
  }, [_vm._v("17")])]), _vm._v(" but William Sanday, who at first gave cautious approval, later withdrew it."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_18",
      "id": "foot-18"
    }
  }, [_vm._v("18")])]), _vm._v(" J. Warschauer attempted to write a biography of Jesus from the viewpoint of Consistent Echatology,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_19",
      "id": "foot-19"
    }
  }, [_vm._v("19")])]), _vm._v(" and B. T. D. Smith interpreted "), _c('em', [_vm._v("The Parables of the Synoptic Gospels")]), _vm._v(" (1937) from this point of view. The one difference between Jesus and Jewish apocalyptists was the former’s single-minded concentration upon the religious element and the complete elimination of the purely national aspect of the hope.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_9"
    }
  }, [_vm._v("In America, Consistent Eschatology has exercised wide influence. As early as 1911, Ernest F. Scott accepted Schweitzer’s interpretation that it was Jesus’ mission to prepare Israel for the imminent inbreaking of the apocalyptic Kingdom. However, contrary to Schweitzer, Scott held that Jesus already realized the law of the coming Kingdom in his own person and that the Kingdom was so imminent that its powers were already manifested in Jesus’ mission. Thus Scott was able to view Jesus’ ethics not as “interim” in character but as an abiding ideal."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_20",
      "id": "foot-20"
    }
  }, [_vm._v("20")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Here is an important emphasis which we have discovered in later German theology. The Kingdom is apocalyptic, future, imminent; but it is in reality not "), _c('em', [_vm._v("exclusively")]), _vm._v(" future. It is so near that its presence can be felt, its powers experienced. This view has become widely accepted. In fact, in 1930, B. S. Easton could write that the controversy about the interpretation of the Kingdom of God had practically reached an end. The Kingdom is the new age, the purely supernatural state of affairs when God alone will rule over men. When the Kingdom comes, history will end. The sayings about the presence of the Kingdom mean that the process of the coming of the Kingdom is already under way. God’s initial act has been performed; and as soon as its consequences have been felt, the next act will follow and the Kingdom will come."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_21",
      "id": "foot-21"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This emphasis upon the nearness of the apocalyptic Kingdom has been frequently repeated. The Kingdom is present only in the sense that the coming clouds in the heavens cast their shadow on the earth."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_22",
      "id": "foot-22"
    }
  }, [_vm._v("22")])]), _vm._v(" Jesus taught an apocalyptic, imminent, future Kingdom, but "), _c('a', {
    attrs: {
      "id": "page_10"
    }
  }, [_vm._v("he was actually engaged in setting up this Kingdom in proleptic fashion."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_23",
      "id": "foot-23"
    }
  }, [_vm._v("23")]), _vm._v(" The Kingdom was already manifesting its signs through Jesus. The reign of God was already breaking in; its full force would be felt in the near future. The bud was visible; the full bloom would shortly be seen."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_24",
      "id": "foot-24"
    }
  }, [_vm._v("24")])]), _vm._v(" The Kingdom was so near that its benefits were even then being partially enjoyed. Long before the sun appears above the horizon, its light dawns on the earth. So the signs of the presence of God’s rule were present."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_25",
      "id": "foot-25"
    }
  }, [_vm._v("25")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Futuristic eschatology is also defended by R. H. Fuller in "), _c('em', [_vm._v("The Mission and Achievement of Jesus")]), _vm._v(". Fuller recognizes only two options: an imminent eschatological Kingdom or Realized Eschatology. He examines the texts which C. H. Dodd used to prove the presence of the Kingdom and concludes that they teach the imminence of the Kingdom, not its presence. “The Kingdom of God has not yet come, but it is near, so near that it is already operative in advance.… The certainty of the event is so overwhelming, the signs of its impendingness so sure, that it is said to have occurred, or to be occurring already.… The Reign of God is already breaking in proleptically in the proclamation and signs of Jesus”; but to say that the Kingdom has come would overstate the case."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_26",
      "id": "foot-26"
    }
  }, [_vm._v("26")])]), _vm._v(" The decisive events lie in the future, not in the past."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_27",
      "id": "foot-27"
    }
  }, [_vm._v("27")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This review of Consistent Eschatology discloses two interesting facts. Some interpret the Kingdom of God as the eschatological act of God, others as the eschatological order, the age to come. In both cases, it is future and apocalyptic. However, many of these scholars are compelled by the language of the Gospels to admit that "), _c('em', [_vm._v("something")]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_11"
    }
  }, [_vm._v("was present: the signs, the dawning, the budding, the shadow of the approaching Kingdom. Furthermore, these interpretations give to the element of imminence a new significance. It is the very imminence of the Kingdom which caused it to cast its shadow ahead. Jesus thought of it as so very near that its presence could be sensed even though it had not yet come. As the morning is preceded by the dawn, as the storm is heralded by the clouds, as the flower is anticipated by the bud, so the Kingdom was near enough that signs of its coming could be seen.")])]), _c('a', {
    attrs: {
      "id": "page_11"
    }
  }, [_c('h1', {
    staticClass: "sec1"
  }, [_vm._v("N"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ONESCHATOLOGICAL")]), _vm._v(" I"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("NTERPRETATIONS")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Not all scholars have been satisfied to accept Schweitzer’s interpretation and to define the Kingdom either exclusively or essentially in apocalyptic terms. Studies have appeared, especially in England and America, which interpret the Kingdom along spiritual noneschatological lines.")])]), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_11"
    }
  }, [_vm._v("Lewis Muirhead attempted to refute the position of Weiss in "), _c('em', [_vm._v("The Eschatology of Jesus")]), _vm._v(" (1904). Apocalyptic imagery is dominated by ethics, not vice versa as Weiss said. The Kingdom is the supreme spiritual reality present in the experience of believers; the futuristic aspect is not important. Ernest von Dobschütz in the "), _c('em', [_vm._v("Transactions of the Third Congress for the History of Religions")]), _vm._v(" argued that apocalyptic was only the form of Jesus’ thought and that there was nothing eschatological about the kernel of Jesus’ religion, which was the rule of God."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_28",
      "id": "foot-28"
    }
  }, [_vm._v("28")]), _vm._v(" In "), _c('em', [_vm._v("The Eschatology of the Gospels")]), _vm._v(", Dobschütz pictured Jesus as sharing contemporary apocalyptic notions but held that his proper view was transmuted eschatology. “What was spoken of in Jewish eschatology as to come in the last days is taken here as already at hand in the lifetime of Jesus …, what was expected as an external change is taken inwardly.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_29",
      "id": "foot-29"
    }
  }, [_vm._v("29")])]), _vm._v(" “In Jesus was fulfilled whatever was expected for the messianic time.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_30",
      "id": "foot-30"
    }
  }, [_vm._v("30")])]), _vm._v(" Apocalyptic is the form "), _c('a', {
    attrs: {
      "id": "page_12"
    }
  }, [_vm._v("in which he interpreted the future success of the Kingdom; the form is wrong but the truth abides. God will rule."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_31",
      "id": "foot-31"
    }
  }, [_vm._v("31")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The most influential book expounding a noneschatological view is the frequently reprinted volume on "), _c('em', [_vm._v("The Teaching of Jesus")]), _vm._v(" by T. W. Manson. The Kingdom in its essence is the reign of God in the experience of the individual soul. In such a personal relationship, questions about time—present or future—are quite irrelevant, for the reign of God within the lives of men is independent of temporal and spatial relations."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_32",
      "id": "foot-32"
    }
  }, [_vm._v("32")])]), _vm._v(" Jesus’ conception of God as Father determined his conception of the Kingdom. The Kingdom is where God’s will is done on earth. Jesus as Messiah realized the Kingdom by utter obedience to God’s will."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_33",
      "id": "foot-33"
    }
  }, [_vm._v("33")])]), _vm._v(" Before Caesarea Philippi, Jesus spoke of the coming of the Kingdom; but after Caesarea Philippi, he spoke of the Kingdom as having come and of people entering it."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_34",
      "id": "foot-34"
    }
  }, [_vm._v("34")])]), _vm._v(" The confession of Peter at Caesarea Philippi was a recognition that the Kingdom of God had come in his person."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_35",
      "id": "foot-35"
    }
  }, [_vm._v("35")])]), _vm._v(" The Kingdom of God is a personal relation between God and the individual."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_36",
      "id": "foot-36"
    }
  }, [_vm._v("36")])]), _vm._v(" The Kingdom may be said to have come with Jesus, for with him occurred “the first manifestation of the Kingdom in the world.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_37",
      "id": "foot-37"
    }
  }, [_vm._v("37")])]), _vm._v(" That is, Jesus was the first to experience the full meaning of the reign of God; his mission was to lead other men into this same experience of the reign of God.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It may seem erroneous to interpret Manson’s view of the Kingdom as noneschatological, for he has a chapter on “The Final Consummation” in which he says, “The ideas of a Judgment, of the elimination of evil from the world, and of a blessed immortality for those who are loyal to God in this life—these ideas are necessary corollaries to the central idea of the Kingdom.… If there is no final victory of good over evil, the Kingdom of God becomes an empty dream.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_38",
      "id": "foot-38"
    }
  }, [_vm._v("38")])]), _vm._v(" However, as Ernst Percy has pointed out,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_39",
      "id": "foot-39"
    }
  }, [_vm._v("39")])]), _vm._v(" these words about an eschatological "), _c('a', {
    attrs: {
      "id": "page_13"
    }
  }, [_vm._v("consummation are not essentially related to Manson’s central concept of the Kingdom. When Manson writes, “The Church is … the army of the Kingdom of God, engaged in the task of conquering every hostile power and winning the world for Christ and ultimately for God,” there is no need for a realistic eschatology to accomplish the final victory of God’s Kingdom. It will be accomplished not by the Parousia of Christ but by the victory of the church in the world."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_40",
      "id": "foot-40"
    }
  }, [_vm._v("40")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Another influential and even more detailed study embodying a noneschatological interpretation is "), _c('em', [_vm._v("The Historic Mission of Jesus")]), _vm._v(" by C. J. Cadoux. The Kingdom of God is man’s acceptance of God’s gracious sovereignty which is essentially timeless,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_41",
      "id": "foot-41"
    }
  }, [_vm._v("41")])]), _vm._v(" for it is the individual’s compliance with God’s will."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_42",
      "id": "foot-42"
    }
  }, [_vm._v("42")])]), _vm._v(" Cadoux rejects in strong language ideas of the Kingdom which place the initiative with God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_43",
      "id": "foot-43"
    }
  }, [_vm._v("43")])]), _vm._v(" The initiative rests with men, for the Kingdom is not the victory of God’s irresistible royal power, but the willing personal acceptance of his fatherly rule by men."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_44",
      "id": "foot-44"
    }
  }, [_vm._v("44")])]), _vm._v(" Jesus was the person through whom the Kingdom became a new reality among men."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_45",
      "id": "foot-45"
    }
  }, [_vm._v("45")])]), _vm._v(" As the obedient son par excellence, Jesus embodied the Kingdom in his own person. His messianic mission was to lead men into the same enjoyment of God’s life which he himself possessed."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_46",
      "id": "foot-46"
    }
  }, [_vm._v("46")])]), _vm._v(" Jesus expected the Jews to accept his message and submit to God’s rule, surrender their attitude of vengeance against Rome, and trust God to frustrate the pagan hostility and to convert Rome’s hostility to friendship."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_47",
      "id": "foot-47"
    }
  }, [_vm._v("47")])]), _vm._v(" This message of God’s Kingdom would then be extended throughout the earth with such success that the time would soon be ripe for God to bring about the great eschatological climax."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_48",
      "id": "foot-48"
    }
  }, [_vm._v("48")])]), _vm._v(" A new race would come into being, "), _c('a', {
    attrs: {
      "id": "page_14"
    }
  }, [_vm._v("and thus God’s Kingdom would come on earth as God’s will is done by all mankind. When Jesus realized his message was to be rejected by the Jews, he accepted the rejection and hoped by his death to bring about the repentance which his preaching had not accomplished. Cadoux interprets apocalyptic as the product of the imaginative Oriental mind which did not understand it literally. Therefore, he feels justified in using a spiritualizing hermeneutic to interpret Jesus’ eschatology."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_49",
      "id": "foot-49"
    }
  }, [_vm._v("49")]), _vm._v(" The permanent value of eschatology is found in the fact that it registers the crucial urgency of those great life-values for which Jesus stood."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_50",
      "id": "foot-50"
    }
  }, [_vm._v("50")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("F. C. Grant, in "), _c('em', [_vm._v("The Gospel of the Kingdom")]), _vm._v(", interprets the Kingdom as a message of social redemption in which the divine sovereignty will be perfectly realized on earth. Jesus expected to see the reign of God established everywhere on earth in his own lifetime. The original gospel of Jesus is the social gospel. The apocalyptic element is due to the disciples’ misinterpretation. They distorted his social view of the Kingdom into an apocalyptic concept, interpreting Jesus in the light of Jewish apocalyptic symbols. Grant thinks that Jesus would indeed have been insane to suppose that he was to come to earth on the clouds to hold judgment and to inaugurate an eschatological Kingdom. Such a view was incompatible with the sanity of Jesus and therefore cannot be attributed to him."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_51",
      "id": "foot-51"
    }
  }, [_vm._v("51")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("H. B. Sharman in a detailed critical study concluded that Jesus taught that the Kingdom was a spiritual reality which was present in his own ministry as God’s mind was expressed through him and a new influence mediated through his personality to others. The apocalyptic element in the Gospels is due to the disciples who neglected Jesus’ warnings about the messianic claimants who would arise during the Jewish war—an event which Jesus foresaw. Jesus’ teachings were misunderstood by the church and misinterpreted in terms of Jewish "), _c('a', {
    attrs: {
      "id": "page_15"
    }
  }, [_vm._v("apocalyptic. This apocalyptic element must be excised and the authentic teachings of Jesus recovered by critical reconstruction."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_52",
      "id": "foot-52"
    }
  }, [_vm._v("52")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A. T. Olmstead reconstructed the true Jesus as an utterly uneschatological figure. “At long last, Jesus makes his own appearance in the full light of history.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_53",
      "id": "foot-53"
    }
  }, [_vm._v("53")])]), _vm._v(" Olmstead’s “historical Jesus” is the antipode of Schweitzer’s. To Olmstead, Jesus was a prophet of righteousness who proclaimed a Kingdom of the Spirit, a living, ever present force in the hearts of its members which at the last is destined to permeate the world. Jesus had read the apocalypses but condemned their fantasies; but the early church misinterpreted Jesus as an apocalyptic figure.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In "), _c('em', [_vm._v("The Religion of Jesus")]), _vm._v(" (1952), Leroy Waterman follows similar lines. Waterman builds on the assumption that apocalyptic and prophetic are two mutually exclusive types of religion. Apocalyptic by definition is nationalistic and particularistic, while prophetic religion is universalistic and ethical. Since Jesus’ religious teaching was spiritual in character, he could not have shared apocalyptic views, for these two types of religion do not blend. The apocalyptic form of Jesus’ teachings is due to the gross misinterpretation of the early church in which his pure religion was smothered in nonspiritual apocalyptic concepts.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Another noneschatological interpretation is the so-called “Prophetic Realism” of John Wick Bowman. Bowman has suggested that this viewpoint is shared by such scholars as T. W. Manson, William Manson, Rudolf Otto, Vincent Taylor, and others."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_54",
      "id": "foot-54"
    }
  }, [_vm._v("54")])]), _vm._v(" However, the views of these men are so diverse that it seems difficult to classify them under a single school of thought.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Bowman’s main concern is to oppose all “apocalyptic” concepts of the Kingdom of God which, according to Bowman’s understanding, are incapable of seeing the activity of the Kingdom on the plane of history. He classes as apocalyptic all views which do not conceive of "), _c('a', {
    attrs: {
      "id": "page_16"
    }
  }, [_vm._v("God as working actively within history. Thus he categorizes as “apocalyptic pessimism” the Kierkegaardian dialectic which defines God as “the wholly other,” the Barthian movement with its American child, Neo-orthodoxy, the Consistent Eschatology of Weiss and Schweitzer, and the modern “fundamentalist aberration.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_55",
      "id": "foot-55"
    }
  }, [_vm._v("55")]), _vm._v(" All of these views are “nonprophetic” in that God never completely reveals himself or brings his Kingdom to completion on the plane of history. Prophetic Realism defines the Kingdom as the personal relationship between man and God, the individual’s experience of God’s sovereignty over his life when he recognizes God’s right to rule and submits his will to God. The Kingdom comes when any man acknowledges God’s sole sovereignty over his life, or when a group of men do the same, or when an ethically conditioned remnant—the church—acknowledge allegiance to God’s sovereignty."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_56",
      "id": "foot-56"
    }
  }, [_vm._v("56")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Bowman’s position is close to the older position of T. W. Manson, whom he frequently quotes; and he admits that this view makes the coming of the Kingdom on earth dependent upon man’s acceptance of God’s Lordship."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_57",
      "id": "foot-57"
    }
  }, [_vm._v("57")])]), _vm._v(" Jesus established the Kingdom on earth; but Bowman leaves it less than clear what we are to understand by this, except that Jesus persuaded men to accept God’s rule."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_58",
      "id": "foot-58"
    }
  }, [_vm._v("58")])]), _vm._v(" The continuing task of the church is to extend the Kingdom in the world—a task which may be described by the phrase “building the Kingdom”—which leads to the redemption of mankind."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_59",
      "id": "foot-59"
    }
  }, [_vm._v("59")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Bowman insists on an absolute contrast between the apocalyptic and prophetic types of religion. In "), _c('em', [_vm._v("The Religion of Maturity")]), _vm._v(", he recognized that these two types are not necessarily exclusive but might be combined."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_60",
      "id": "foot-60"
    }
  }, [_vm._v("60")])]), _vm._v(" However, his last study in this area, "), _c('em', [_vm._v("Prophetic Realism and the Gospel")]), _vm._v(", develops one line of thought appearing in his earlier writings and excludes altogether every vestige of apocalyptic "), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }, [_vm._v("from prophetic religion. Apocalyptic religion, apparently by definition, can talk to God only at long range and is incapable of a concept of the Kingdom which is active on the plane of history. It is altogether otherworldly and transcendental. In an earlier book, "), _c('em', [_vm._v("The Intention of Jesus")]), _vm._v(", Bowman argued that apocalyptic concepts provided the shell which enclosed the kernel of Jesus’ deepest conviction."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_61",
      "id": "foot-61"
    }
  }, [_vm._v("61")]), _vm._v(" Jesus made only a formal use of these apocalyptic frames, or they were simply an accommodation on our Lord’s part to the thought-forms of his hearers. Later, in "), _c('em', [_vm._v("The Religion of Maturity")]), _vm._v(", he wrote that apocalyptic concepts were used as “intellectual bait.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_62",
      "id": "foot-62"
    }
  }, [_vm._v("62")])]), _vm._v(" These molds must not be confused with the gospel which Jesus lived and preached. The church has no need of apocalyptic, which Bowman calls “the religion of the throne,” for her function is not to judge the world, but with her Lord and under his leadership to effect its salvation."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_63",
      "id": "foot-63"
    }
  }, [_vm._v("63")])]), _vm._v(" Apparently, the consummation of the Kingdom in Bowman’s view is nothing less than a world which has been brought into submission to God’s rule through the church’s proclamation of the gospel."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_64",
      "id": "foot-64"
    }
  }, [_vm._v("64")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Recent biblical scholarship in Great Britain has reacted strongly against the one-sided eschatological views of Schweitzer, and in the writings of C. H. Dodd has given the concept of eschatology a new content. Eschatology has to do not with the last things, temporally conceived, but with those things which possess finality and ultimacy of meaning. The Kingdom of God does not mean the eschatological order at the end of history, but the eternally present realm of God. The coming of the Kingdom means the entrance of the eternal into time, the confrontation of the finite by the infinite, the intrusion of the transcendental into the natural. The Kingdom of God is timeless, "), _c('a', {
    attrs: {
      "id": "page_18"
    }
  }, [_vm._v("eternal, transcendental, and is therefore always near and always laying its demands upon men. Apocalyptic language is merely an ancient idiom in which this timeless religious truth expressed itself."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_65",
      "id": "foot-65"
    }
  }, [_vm._v("65")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Dodd has not only interpreted the teachings of Jesus in terms of Realized Eschatology, but has also reconstructed the history of primitive eschatology from this point of departure. Dodd takes as his clue for the basic meaning of the Kingdom those characteristic and distinctive sayings about the presence of the Kingdom which are without parallel in Jewish teachings."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_66",
      "id": "foot-66"
    }
  }, [_vm._v("66")])]), _vm._v(" Jesus saw the Kingdom present in his own life, death, resurrection, ascension, and Parousia. These are not to be taken as several events but were in the thought of Jesus facets of a single complex event. For Jesus, there was a single day of victory, and it was occurring in his ministry. Dodd tries to prove that "), _c('em', [_vm._v("eggiken")]), _vm._v(" in Matthew 4:17 is synonymous with "), _c('em', [_vm._v("ephthasen")]), _vm._v(" in Matthew 12:28 and means “has come.” Jesus’ proclamation was not, “The Kingdom of God has drawn near,” but “The Kingdom of God has come.” The parables teach a crisis of judgment which is not future but present. The long period of growth is over, the hour of decision has arrived. All that remains is to put in the sickle and reap the harvest. The resurrection, exaltation, and second advent in Jesus’ thought are three aspects of one idea. It is worthy of more than passing interest that this position, which is a crucial element in Dodd’s reconstruction, is admitted to be “no more than a speculative conjecture” about which “there is nothing conclusive.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_67",
      "id": "foot-67"
    }
  }, [_vm._v("67")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("When the Parousia did not occur, the early church separated it from the resurrection, thus making two days of what Jesus had seen as one. It reinterpreted the message of Jesus along the lines of Jewish apocalyptic. Paul in the Thessalonian epistles at first took over this Jewish eschatology but later reinterpreted eschatology in terms of "), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("mysticism. The problem created by the delay of the Parousia was thus solved by Paul’s renewed apprehension of Realized Eschatology. This rediscovery the Fourth Gospel carried further by completely refining away the crude eschatological elements of a futurist eschatology. Thus it happens that the Gospel furthest removed from Jesus in time contains the nearest to the true substance of his teaching. The Realized Eschatology of Paul means that “all that prophecy and apocalypse had asserted of the supernatural messianic community was fulfilled in the Church.” In the Fourth Gospel, “all that the church hoped for in the second coming of Christ is already given in its present experience of Christ through the Spirit.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_68",
      "id": "foot-68"
    }
  }, [_vm._v("68")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("While Dodd rejects the futuristic and apocalyptic concepts of Paul, he finds the true meaning of the Kingdom of God in Jesus’ use of apocalyptic language. Apocalyptic is “a series of symbols standing for realities which the human mind cannot directly apprehend.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_69",
      "id": "foot-69"
    }
  }, [_vm._v("69")])]), _vm._v(" The new world of apocalyptic thought is in reality “the transcendent order beyond space and time.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_70",
      "id": "foot-70"
    }
  }, [_vm._v("70")])]), _vm._v(" Jesus employed the traditional symbolism of apocalypse to indicate the otherworldly or absolute character of the Kingdom of God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_71",
      "id": "foot-71"
    }
  }, [_vm._v("71")])]), _vm._v(" “The thought of Jesus passed directly from the immediate situation to the eternal order lying beyond all history, of which he spoke in the language of apocalyptic symbolism.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_72",
      "id": "foot-72"
    }
  }, [_vm._v("72")])]), _vm._v(" Apocalyptic concepts such as judgment, bliss, the Son of Man, etc., are “eschatological” in character; that is, “they are ultimates, and are proper not to this empirical realm of time and space but to the absolute order.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_73",
      "id": "foot-73"
    }
  }, [_vm._v("73")])]), _vm._v(" Thus the essence of Jesus’ teaching is that “the ultimate, the Kingdom of God, has come into history … the absolute, the ‘wholly other’ has entered into time and space … ‘the day of the Son of Man’ stands for the timeless fact. So far as "), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_vm._v("history can contain it, it is embodied in the historic crisis which Jesus brought about.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_74",
      "id": "foot-74"
    }
  }, [_vm._v("74")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Before we leave Dodd, we should note that it is usually said that Dodd has modified his view of a completely realized eschatology to admit that there is a residue of unrealized or futuristic eschatology. This impression is based on Dodd’s acceptance of Joachim Jeremias’ criticism that he is one-sided in his Realized Eschatology and insists on an unnecessary contraction of eschatology."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_75",
      "id": "foot-75"
    }
  }, [_vm._v("75")])]), _vm._v(" Instead of speaking of Realized Eschatology, Jeremias suggests the expression, “eschatology in process of realization.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_76",
      "id": "foot-76"
    }
  }, [_vm._v("76")])]), _vm._v(" While Dodd accepts the validity of this criticism,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_77",
      "id": "foot-77"
    }
  }, [_vm._v("77")])]), _vm._v(" a careful reading of his book, "), _c('em', [_vm._v("The Coming of Christ")]), _vm._v(", leaves his position in doubt. He says, “At the last frontier-post we shall encounter God in Christ”;"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_78",
      "id": "foot-78"
    }
  }, [_vm._v("78")])]), _vm._v(" but what this seems to mean is expressed in another sentence: “When each individual person reaches the frontier post of death, he steps into the presence of the Eternal. And when in due course history ends, and the human race perishes from this planet, it will encounter God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_79",
      "id": "foot-79"
    }
  }, [_vm._v("79")])]), _vm._v(" This sounds more like Greek immortality than the biblical hope of the Kingdom of God which is concerned with history.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("However, in one of his most recent books, Dodd seems to allow for a real futurity of the Kingdom. In "), _c('em', [_vm._v("The Founder of Christianity")]), _vm._v(" (1970), he speaks of “the final victory of God’s cause over all the powers in the universe.… Jesus … was pointing to the final victory of God’s cause, or in other words, the consummation of his kingdom, beyond history” ("), _c('span', {
    attrs: {
      "href": "#/014_Chapter04#page_117",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 117")]), _vm._v("). “The Kingdom of God, while it is present experience, remains also a hope, but a hope directed to a consummation beyond history” ("), _c('span', {
    attrs: {
      "href": "#/014_Chapter04#page_115",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 115")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("We have previously described Rudolf Bultmann’s interpretation "), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_vm._v("as one which agrees essentially with Consistent Eschatology. However, Bultmann does not find the real "), _c('em', [_vm._v("meaning")]), _vm._v(" of Jesus’ teaching in an imminent apocalyptic kingdom. This apocalyptic expectation was in fact only the form in which his deepest religious conviction expressed itself. Apocalyptic ideas of a future Kingdom are mythological, but in this mythology is embodied an existential meaning. The central reality in Jesus’ experience was an overpowering consciousness of the sovereignty of God, the absoluteness of his will. Before this consciousness of God, the world sinks away and seems to be at its end. Jesus’ expectation of the immediate end of the world is not the core of his message but is merely the reflex of his God-consciousness. “The essential thing about the eschatological message is the idea of God that operates in it and the idea of human existence that it contains—not the belief that the end of the world is just ahead.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_80",
      "id": "foot-80"
    }
  }, [_vm._v("80")]), _vm._v(" Thus Bultmann cannot finally be classed with Schweitzer and Weiss, for whom the imminent end of the world is the essence of Jesus’ eschatological message. For Bultmann, Jesus’ mythological eschatological expectation is really incidental. The essential thing is the demand of God embodied therein. Thus the Kingdom is “wholly other,” supernatural, suprahistorical; i.e.; it is “eschatological.” The real meaning of the Kingdom is independent of the mythological terms in which Jesus described it. It is rather the “transcendent event, which signifies for man the ultimate Either-Or, which constrains him to decision.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_81",
      "id": "foot-81"
    }
  }, [_vm._v("81")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This existential interpretation of the Kingdom has had wide influence. F. C. Grant, whose earlier study emphasized the social gospel, has used the word “eschatological” to describe not future but final realities. The idea of Messiah is eschatological. “It stands for the "), _c('em', [_vm._v("finality")]), _vm._v(" of God’s revelation in Christ, the finality of God’s act in Christ.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_82",
      "id": "foot-82"
    }
  }, [_vm._v("82")])]), _vm._v(" New Testament theology is from beginning to end eschatologically conditioned: i.e., it has to do with the “suprahistorical; "), _c('a', {
    attrs: {
      "id": "page_22"
    }
  }, [_vm._v("not so much ‘beyond history’ (after the end of this present age …) as ‘above’ history, outside time and place, in the ever-present ‘now’ of eternity—in the Mind of God.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_83",
      "id": "foot-83"
    }
  }, [_vm._v("83")]), _vm._v(" Jesus’ conception of the Kingdom of God as virtually present left little room for a universal judgment. In reality, judgment essentially is “not a dramatic episode at the end of history, but the confrontation of sin and every evil with the goodness, the holiness, the mercy and love of God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_84",
      "id": "foot-84"
    }
  }, [_vm._v("84")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("John Knox recognizes in Jesus’ teaching both a present and a future element, and he thinks that Dodd’s view of completely realized eschatology is impossible to ascribe to Jesus. Knox believes that Jesus looked for a new order in the immediate future; the element of the temporal cannot be eliminated. But one is not distorting or modernizing Jesus’ teaching “when one denies that even for him [Jesus] the whole meaning of the immediacy of the Kingdom was exhausted by the expected future crisis. The Kingdom of God could be thought of as imminent in the future only because in another sense it is continually present. It "), _c('em', [_vm._v("will")]), _vm._v(" come soon because it "), _c('em', [_vm._v("is")]), _vm._v(" near. The Kingdom, in this absolute sense, did not come soon—it did not come at all—but it is still near.… The Kingdom of God is always at hand, not as a future event, perhaps, but in the profounder sense of an ever present reality, both within our life and above it, both immanent and transcendent.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_85",
      "id": "foot-85"
    }
  }, [_vm._v("85")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In "), _c('em', [_vm._v("Jesus, the Messiah")]), _vm._v(", William Manson vigorously discussed the messianic question in light of Form Criticism and interpreted the Kingdom of God in noneschatological terms, similar to Bultmann. Manson claims that the central fact in our Lord’s ministry was his intense consciousness of God. This consciousness led him to reason from the present to the future. His awareness of the holiness and the power of God already at work in the world led him to make certain deductions about the coming of the Kingdom. His announcement of this coming Kingdom took the form of an imminent supernatural event. However, the concept of God as Father was prior to his eschatology "), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("and produced it. His vision of the coming Kingdom is a projection of the intense inwardness of his spirit’s realization of the presence of God. Thus the consciousness of the presence of God was the central reality; the apocalyptic prospect of the Kingdom was merely the form in which this consciousness expressed itself. The abiding meaning of eschatology is to be found in “a spiritual encounter of our souls with him in which Jesus of Nazareth so interprets and represents to us the meaning of the Eschaton or reign of God that henceforth there can be for us no separation of the messianic good from the revelation of God in the spirit of Jesus.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_86",
      "id": "foot-86"
    }
  }, [_vm._v("86")]), _vm._v(" Eschatology is “the sign or token of the inner transformation of men and of world-history.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_87",
      "id": "foot-87"
    }
  }, [_vm._v("87")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("T. W. Manson has expressed similar views in one of his later works, "), _c('em', [_vm._v("The Servant-Messiah")]), _vm._v(". Jesus’ eschatology contains the “real Eschaton” which announces not the latest development in the historical process but “the last.” This last development possesses a finality “which makes time-measurement irrelevant.” Furthermore, this finality has invaded the present moment. The first meaning of Realized Eschatology emerges in the fact that the distinguishing quality of the Eschaton is not postremity but finality."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_88",
      "id": "foot-88"
    }
  }, [_vm._v("88")])]), _vm._v(" The Kingdom of God is “the actualization in history of God’s power and wisdom as the secret of all true human welfare.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_89",
      "id": "foot-89"
    }
  }, [_vm._v("89")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec1"
  }, [_vm._v("S"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("YNTHESIS OF")]), _vm._v(" F"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("UTURISTIC AND")]), _vm._v(" R"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("EALIZED")]), _vm._v(" E"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("SCHATOLOGY")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("We have traced the course of recent criticism in eschatological views which interpret the Kingdom of God as the future apocalyptic realm and in noneschatological views which see the Kingdom of God "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("as an experience of God in the present for which eschatology is quite unnecessary. However, between these two extremes stands a group of scholars who recognize the necessity of both the present and the future, the “spiritual” and the “eschatological” aspects of the Kingdom. The first four of these studies appeared within a period of five years from 1929–1934.")])]), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("In 1928, Gerhard Gloege published a slim inaugural dissertation, later enlarged into a thorough study of the Kingdom of God,"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_90",
      "id": "foot-90"
    }
  }, [_vm._v("90")]), _vm._v(" in which he employed a fresh approach. The Kingdom of God by definition is not the eschatological realm of salvation, the age to come, but it is an abstract concept designating the royal rule of God, his kingly activity. Gloege rejected any local or “static” interpretation of the Kingdom as a “realm” or a “renovated world.” God’s Kingdom is his rule, active among men for their redemption. In one sense, the Kingdom is eternal; but in another sense, it is manifested in time and in history. The Kingdom is present because the rule of God is present in the person and deeds of Jesus; but in its full manifestation, the Kingdom is future. Gloege developed the concept that the present rule of God demands a soteriology and an ecclesiology.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("H. D. Wendland, following a similar line of thought, found the unity of the present and future aspects of the Kingdom in the recognition that it is "), _c('em', [_vm._v("God’s")]), _vm._v(" Kingdom and is therefore both future and transcendental. Because it is transcendental, it can also be present."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_91",
      "id": "foot-91"
    }
  }, [_vm._v("91")])]), _vm._v(" The Kingdom is indeed eschatological and future as "), _c('em', [_vm._v("Endzeitlichkeit")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_92",
      "id": "foot-92"
    }
  }, [_vm._v("92")])]), _vm._v(" Wendland fully recognizes the eschatological character of Jesus’ preaching."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_93",
      "id": "foot-93"
    }
  }, [_vm._v("93")])]), _vm._v(" But the eschatological character of the Kingdom does not exhaust its meaning; it is also transcendental and eternal ("), _c('em', [_vm._v("überzeitlich, ewig")]), _vm._v("), and therefore can be near and present."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_94",
      "id": "foot-94"
    }
  }, [_vm._v("94")])]), _vm._v(" Wendland "), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("takes his point of departure from the futurity and eschatological character of the Kingdom ("), _c('em', [_vm._v("Zukünftigkeit")]), _vm._v(" and "), _c('em', [_vm._v("Endzeitlichkeit")]), _vm._v("); but he also insists on “an inner polarity of the concept of the Kingdom in its futurity and transcendence ("), _c('em', [_vm._v("Endzeitlichkeit")]), _vm._v(" and "), _c('em', [_vm._v("Überzeitlichkeit")]), _vm._v("),” in which the presence of the Kingdom is to be understood as a real and essential element and not merely as a psychological anticipation."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_95",
      "id": "foot-95"
    }
  }, [_vm._v("95")]), _vm._v(" The transcendent character of the future Kingdom in Wendland’s thought may be illustrated when he describes futurity as “the coming to us of God” ("), _c('em', [_vm._v("Zu-uns-kommen Gottes")]), _vm._v(")."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_96",
      "id": "foot-96"
    }
  }, [_vm._v("96")])]), _vm._v(" The future Kingdom, because it is transcendental, has broken into the present world-order, demanding decision of men and manifesting itself in Jesus’ works and person. The question of the Kingdom is thus inseparable from Christology; for as the future Messiah is already present among men, in him the future rule of God is present. In Christ, the Kingdom is both present and future.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Rudolf Otto’s influential "), _c('em', [_vm._v("The Kingdom of God and the Son of Man")]), _vm._v(" understands the Kingdom of God to be the heavenly realm where God’s will is done, the suprahistorical sphere where God rules. Jesus’ teaching is grounded in a dualism of earth vs. heaven. The heavenly realm is a “wholly other” existence, and Jesus announced the coming of this miraculous supernatural realm. This event is exclusively God’s deed and will mean the breaking off of history and the descent of the heavenly realm to earth. The Kingdom of heaven will come down from above and effect a marvelous transformation of the world. The Lord’s Prayer is a petition for the coming of this supernatural, heavenly realm.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("However, Jesus believed that the Kingdom was already in process of coming. This he believed because in a vision he had seen Satan overthrown in heaven (Luke 10:18). Therefore, he knew that God had already achieved victory over Satan and that the Kingdom had already been realized in heaven."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_97",
      "id": "foot-97"
    }
  }, [_vm._v("97")])]), _vm._v(" A tidal wave of divine victory had been set up by virtue of which the powers of the Kingdom were "), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("already operative on earth. This is what Otto means by his oft-quoted statement, “It is not Jesus who brings the kingdom …, the kingdom brings him with it.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_98",
      "id": "foot-98"
    }
  }, [_vm._v("98")]), _vm._v(" The future heavenly realm is already breaking into the world through Jesus in the form of wonderful, supernatural, coercive power operating from above."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_99",
      "id": "foot-99"
    }
  }, [_vm._v("99")])]), _vm._v(" The Kingdom is not only the eschatological realm; it is also victorious, coercive power."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_100",
      "id": "foot-100"
    }
  }, [_vm._v("100")])]), _vm._v(" The eschatological realm of salvation is already breaking into the world as a divine dynamis."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_101",
      "id": "foot-101"
    }
  }, [_vm._v("101")])]), _vm._v(" In the future age Jesus will become the heavenly Son of Man; but he is already the agent of the present inbreaking power of the Kingdom.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In 1930, Joachim Jeremias published a provokingly short but suggestive study which may be translated, "), _c('em', [_vm._v("Jesus the Consummation of the World")]), _vm._v(", in which the Kingdom of God is both present and future. Jeremias states that Jesus was the Messiah, the King of the "), _c('em', [_vm._v("Gottesherrshaft")]), _vm._v(". It was Jesus’ mission to bring the world to its consummation in two steps: humility and glory. In his humility, Jesus appeared with the claim that with his coming, the consummation of the world has broken in."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_102",
      "id": "foot-102"
    }
  }, [_vm._v("102")])]), _vm._v(" The Spirit had once more become active among men, the new creation had begun (Luke 4:1 ff.; Matt. 11:4–5), the new wine—a symbol of the era of salvation—was offered. The King entered Jerusalem and the temple was cleansed. Jeremias finds messianic significance in words and acts of Jesus through which Jesus claimed that the age of salvation had come and that the heavenly Son of Man, the renewer of the world, was among men. Satan was being overthrown. Yet because the King was present in humility, his role in bringing the age of salvation ("), _c('em', [_vm._v("Weltvollendung")]), _vm._v(") was apparent only to the eye of faith. He was destined to be manifested in glory. At the Parousia, Satan would finally be destroyed and the old world judged by fire, a new world emerge, and the holy community enjoy the messianic feast.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("A clearer picture of some aspects of Jeremias’ views emerges in "), _c('em', [_vm._v("The Parables of Jesus")]), _vm._v(". The key to the meaning of the Kingdom is Christology. Jesus not only utters the message of the Kingdom of God, he is himself the message."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_103",
      "id": "foot-103"
    }
  }, [_vm._v("103")]), _vm._v(" It is this fact which enables Jeremias to see messianic meaning in so many of Jesus’ acts. His deeds as well as his words are parables. Jeremias holds that Jesus expected the eschatological consummation to take place in the near future. Jesus in fact lived in the expectation of the final "), _c('em', [_vm._v("peirasmos")]), _vm._v(" (Mark 14:48), the last crisis of history which his death would introduce."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_104",
      "id": "foot-104"
    }
  }, [_vm._v("104")])]), _vm._v(" The eschatological tribulation, the last attack of Satan upon the saints of God, would begin at his passion,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_105",
      "id": "foot-105"
    }
  }, [_vm._v("105")])]), _vm._v(" which would be the warning of the imminent catastrophe."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_106",
      "id": "foot-106"
    }
  }, [_vm._v("106")])]), _vm._v(" Jesus would usher in the new age; but the way to the new creation lay through disaster and destruction."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_107",
      "id": "foot-107"
    }
  }, [_vm._v("107")])]), _vm._v(" Jesus’ death would not only usher in the end, it would also create the redeemed community. His vicarious death was a ransom for the innumerable host of the redeemed."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_108",
      "id": "foot-108"
    }
  }, [_vm._v("108")])]), _vm._v(" The relationship of the Kingdom as present to its future consummation is given in the explanation that the presence of salvation is only the prelude to the future consummation,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_109",
      "id": "foot-109"
    }
  }, [_vm._v("109")])]), _vm._v(" and is designed to bring men to a decision in view of, and in preparation for, the imminent end.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("T. W. Manson’s noneschatological interpretation of the Kingdom of God in "), _c('em', [_vm._v("The Teaching of Jesus")]), _vm._v(" has been outlined above (")]), _c('a', {
    attrs: {
      "href": "#/009_Chapter01#page_12"
    }
  }, [_vm._v("p. 12")]), _vm._v("). In a later commentary on the sayings of Jesus,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_110",
      "id": "foot-110"
    }
  }, [_vm._v("110")])]), _vm._v(" Manson reflects the influence of Rudolf Otto and presents an interpretation of the Kingdom whose center is the act of God rather than the experience of man. He is therefore able to combine the present and future aspects of the Kingdom more successfully than in his earlier work. The Kingdom of God is still the realization of the will of God on earth; but the will of God expresses itself in “a Divine act for men.” The ministry of Jesus is “the Kingdom at work in the world.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_111",
      "id": "foot-111"
    }
  }, [_vm._v("111")])]), _vm._v(" The Kingdom shows its power in the work of Jesus and his messengers by overcoming those forces of evil which degrade and destroy man."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_112",
      "id": "foot-112"
    }
  }, [_vm._v("112")])]), _vm._v(" This act of God has created a new period in history which succeeds that of the law and the prophets: the period of the Kingdom of God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_113",
      "id": "foot-113"
    }
  }, [_vm._v("113")])]), _vm._v(" Thus the Kingdom is a state of affairs into which men may enter rather than a state of mind."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_114",
      "id": "foot-114"
    }
  }, [_vm._v("114")])]), _vm._v(" The Kingdom is both present and future. Jesus set in motion supernatural forces which go on inevitably until they reach their consummation. “The consummated Kingdom is now present in the world as really as the harvest is present in the sown field. How the growth takes place and when it will come to full fruition—these are questions which the sower cannot answer. They are God’s affair. But that heavenly forces, as real as the mysterious forces of nature, are already at work in the world, moving inevitably to the great consummation—of that Jesus had no doubt whatever. The Kingdom is a present reality working toward a future consummation.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_115",
      "id": "foot-115"
    }
  }, [_vm._v("115")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }, [_vm._v("This consummation which would bring the death of the present order, Jesus expected to come “quickly, suddenly, and completely.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_116",
      "id": "foot-116"
    }
  }, [_vm._v("116")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Future and present are combined by Jean Héring in "), _c('em', [_vm._v("Le Royaume de Dieu et sa Venue")]), _vm._v("; but unfortunately, the study is too brief to be very helpful. The Kingdom is the imminent apocalyptic Kingdom which is so near that it casts its shadow into the present. In fact, says Héring, one must even say that the Kingdom was actually realized, although on a moral level, in the hearts of those who accepted the announcement of the coming Kingdom with faith and repentance. Thus Jesus taught that in his prediction there was present an invisible germ of the Kingdom."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_117",
      "id": "foot-117"
    }
  }, [_vm._v("117")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("One of the most important recent studies is W. G. Kümmel’s "), _c('em', [_vm._v("Promise and Fulfilment")]), _vm._v(". For Kümmel, the Kingdom of God by definition is the future eschatological age, the Eschaton which Jesus expected to appear in the near future. However, Kümmel takes a decisive step beyond most writers who hold this view by recognizing that the Eschaton was already present in Jesus’ person. Strongly opposing recent noneschatological and existential interpretations, Kümmel first establishes that Jesus announced the imminent coming of the eschatological Kingdom. It will come within a generation, but no one knows exactly when. However, Jesus saw this future eschatological consummation to be effective in his own person."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_118",
      "id": "foot-118"
    }
  }, [_vm._v("118")])]), _vm._v(" The future Kingdom had already begun its activity."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_119",
      "id": "foot-119"
    }
  }, [_vm._v("119")])]), _vm._v(" Kümmel strictly limits this present manifestation of the Kingdom to Jesus’ person. It is quite mistaken to think that the actual Kingdom of God was present in the fellowship or activity of Jesus’ disciples."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_120",
      "id": "foot-120"
    }
  }, [_vm._v("120")])]), _vm._v(" Jesus had no thought of gathering around him a new congregation in whom the Kingdom of "), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }, [_vm._v("God would be present."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_121",
      "id": "foot-121"
    }
  }, [_vm._v("121")]), _vm._v(" Jesus himself was the sole sign of the coming Kingdom.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Like Goguel, Kümmel differentiates between apocalyptic and prophetic eschatology. Jesus’ preaching about the imminent future Kingdom is eschatological promise, not apocalyptic instruction;"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_122",
      "id": "foot-122"
    }
  }, [_vm._v("122")])]), _vm._v(" a prophetic message, not an apocalyptic revelation."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_123",
      "id": "foot-123"
    }
  }, [_vm._v("123")])]), _vm._v(" The difference between eschatology and apocalyptic, Kümmel finds in the “complete absence of any delineation of eschatological conditions.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_124",
      "id": "foot-124"
    }
  }, [_vm._v("124")])]), _vm._v(" Apocalyptic desires to throw light on eschatological proceedings; eschatology is concerned with the destiny of men in view of the impending judgment. Apocalyptic reveals future events; eschatology prepares men for the future. Sayings which embody apocalyptic instruction, such as many verses in Mark 13, must be ascribed to Jewish or Jewish-Christian apocalyptic rather than to Jesus. Therefore, Jesus’ "), _c('em', [_vm._v("Weltanschauung")]), _vm._v(" stands in complete contrast to that of apocalyptic, for the significance of his proclamation of the imminent end of the world does not lie in the apocalyptic description, but in the fact that men are now summoned to prepare for the end, for they are confronted “with the end of history as it advances toward the goal set by God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_125",
      "id": "foot-125"
    }
  }, [_vm._v("125")])]), _vm._v(" The real meaning of imminence is not in temporality, but in the certainty of the future and its impact on the present.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The position of A. N. Wilder in his important study "), _c('em', [_vm._v("Eschatology and Ethics in the Teaching of Jesus")]), _vm._v(" is difficult to analyze. It is easy to understand why Kümmel classes Wilder among those scholars who attempt to get rid of a future eschatological consummation."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_126",
      "id": "foot-126"
    }
  }, [_vm._v("126")])]), _vm._v(" Wilder says that Jesus was a this-worldly prophet and not the otherworldly Son of Man of Schweitzer’s interpretation. Jesus saw a great process under way which was moving toward a climax, and thought himself to be the prophet of the new order. Jesus anticipated a new and revolutionary this-worldly order, and he used apocalyptic language to "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }, [_vm._v("lend urgency and significance to his message."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_127",
      "id": "foot-127"
    }
  }, [_vm._v("127")]), _vm._v(" However, like Jewish apocalyptists, he did not intend this apocalyptic language to be taken literally."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_128",
      "id": "foot-128"
    }
  }, [_vm._v("128")])]), _vm._v(" Apocalyptic is language symbolic of the historical crisis which was in process."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_129",
      "id": "foot-129"
    }
  }, [_vm._v("129")])]), _vm._v(" Jesus’ “forecast had to do with ultimates; and it rested on ultimates. It had to do with last things, and it rested on first things. But it was directed to the present moment and to the actual scene and was lived out in the concrete process of history, and it bore on that concrete process in its future aspects.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_130",
      "id": "foot-130"
    }
  }, [_vm._v("130")])]), _vm._v(" Jesus’ forecasts of the judgment and of the Kingdom were only representations of the “unprophesiable, unimaginable but certain, God-determined future. This future and God’s action in it lend immense weight and urgency to their present moral responsibility. Yet this temporal imminence of God is but a function of his spiritual imminence, and it is this latter which really determines conduct.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_131",
      "id": "foot-131"
    }
  }, [_vm._v("131")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This seems clear enough. Apocalyptic language is merely a symbolic form by which Jesus described the expected historical crisis. This appears to be a rather complete historization of eschatology. But on the other hand, Wilder expressly denies that he wishes to rule out eschatology altogether."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_132",
      "id": "foot-132"
    }
  }, [_vm._v("132")])]), _vm._v(" He criticizes such scholars as C. C. McCown and F. C. Grant for denying any transcendental element in Jesus’ future perspective and for interpreting Jesus’ hope in altogether this-worldly terms."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_133",
      "id": "foot-133"
    }
  }, [_vm._v("133")])]), _vm._v(" He insists that justice must be done to the Christian view of the new age as a “corporate matter” which “takes up into itself the historical life of man and its significant gains and is, therefore, by no means unrelated to our life in this age.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_134",
      "id": "foot-134"
    }
  }, [_vm._v("134")])]), _vm._v(" He approves of Walter Künneth’s thesis"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_135",
      "id": "foot-135"
    }
  }, [_vm._v("135")])]), _vm._v(" “that the Kingdom of God in the age to come is a fulfillment of historical existence and not its "), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_vm._v("abridgment.…”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_136",
      "id": "foot-136"
    }
  }, [_vm._v("136")]), _vm._v(" In a recent essay, Wilder clearly states that the eschatological event must include the redemption of creation as well as the total redemption of man."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_137",
      "id": "foot-137"
    }
  }, [_vm._v("137")])]), _vm._v(" Wilder’s thesis appears to be that Jesus foresaw a historical crisis in the near future which he described in apocalyptic language to lend it urgency; but beyond the foreseeable historical crisis lay the eschatological crisis which was ineffable"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_138",
      "id": "foot-138"
    }
  }, [_vm._v("138")])]), _vm._v(" and therefore could be described only in the symbolic language of apocalyptic. To Wilder, eschatology by definition is myth in that it represents the unknown future but nevertheless asserts the sovereignty of God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_139",
      "id": "foot-139"
    }
  }, [_vm._v("139")])]), _vm._v(" The future lay beyond Jesus’ knowledge. He used apocalyptic concepts to express his confidence that the final outcome would be decided by the power of God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_140",
      "id": "foot-140"
    }
  }, [_vm._v("140")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("However, the Kingdom was present as well as future. Wilder follows Otto in suggesting that Jesus felt himself to be part of a great redemption-transaction, a world-process under way and moving toward its climax. Jesus sometimes pictured the outcome of this process in apocalyptic terms, but he also pictured this process in less dualistic fashion in terms of a historical future."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_141",
      "id": "foot-141"
    }
  }, [_vm._v("141")])]), _vm._v(" Therefore Jesus conceived of the period that began with John and included his own work as the time of salvation which introduced a new situation. Jesus’ ethics were determined primarily by this new time of salvation. The eschatological sanction exists, but it is only formal and secondary. The determinative fact is the spiritual imminence of God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_142",
      "id": "foot-142"
    }
  }, [_vm._v("142")])]), _vm._v(" Thus, while Wilder’s position is at times ambiguous,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_143",
      "id": "foot-143"
    }
  }, [_vm._v("143")])]), _vm._v(" we conclude that he intends to recognize both present and future elements in Jesus’ teachings.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("A. M. Hunter recognizes that the Kingdom is both present and future, although one could wish that he had developed in greater detail the meaning of the future aspect."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_144",
      "id": "foot-144"
    }
  }, [_vm._v("144")]), _vm._v(" The Kingdom is first dynamic—the kingly rule of God; second, it is eschatological—the consummation of the divine purpose for men. The Jews conceived of this purpose at the end of history; Jesus said that the end had projected itself into history. Hunter prefers the term “inaugurated eschatology” to Dodd’s “realized eschatology.” Hunter follows Dodd in interpreting "), _c('em', [_vm._v("eggiken")]), _vm._v(" to mean “has arrived”; “the appointed time has fully come.” Like Dodd, he also sees in the parables the teaching of a present harvest, not an eschatological judgment. The Kingdom is God’s reign becoming effective in human affairs. It is God visiting and redeeming his people. The miracles were the Kingdom in action. The Kingdom which is centered in Christ means a new relationship to God, implies a new Israel, involves a new pattern for living, and requires Jesus’ death. The Kingdom is also an eternally existent order above history where God’s rule is always present; but this Kingdom which has come into history in the person of Jesus will come at the end of the existing order and will involve the breakdown of the physical universe and the emergence of another world beyond history. Hunter insists that it is impossible to set aside the expectation of a coming of Christ beyond history and an eschatological consummation of the Kingdom of God.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A somewhat different approach to the problem has been used by Ernst Percy in "), _c('em', [_vm._v("Die Botschaft Jesu")]), _vm._v(" (1953). He seeks to solve the problem of presence and futurity by asking whether Jesus’ mission was to prepare men for a future Kingdom or to grant them participation in a present Kingdom. Did Jesus preach primarily repentance or offer salvation? Percy feels that scholars like Bousset and Weiss started with a false premise by confusing the concept of the Kingdom of God with that of the messianic Kingdom. The latter is "), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_vm._v("political and national whereas the former is a religious concept: the eschatological rule of God. Judaism never used “the Kingdom of God” as a term for the eschatological salvation of the age to come, as Jesus did. The basic question is whether this eschatological salvation is exclusively future or in some real sense present. Percy solves the problem by a careful study of Jesus’ preaching about repentance and salvation, especially in the Sermon on the Mount, and concludes that the Sermon is not primarily a call to repentance but an illustration of the perfect piety of the Kingdom. This piety ("), _c('em', [_vm._v("Frömmigkeit")]), _vm._v(") is the gift of salvation ("), _c('em', [_vm._v("Heilsgabe")]), _vm._v(") which is given to the poor because the Kingdom is present.")])]), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_c('p', {
    staticClass: "indent-para"
  }, [_vm._v("From this indirect line of approach, Percy concludes that while the Kingdom of God is the perfect eschatological rule ("), _c('em', [_vm._v("Herrschaft")]), _vm._v(") of God, it is actually present in the gift of salvation which brings men into a new fellowship with God. In the person of Jesus, the Kingdom has already begun, Satan has been defeated, men are being delivered from demonic power, and the good news of salvation is received by the poor.")])]), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_vm._v("The influence of contemporary biblical theology upon Catholic scholarship is well illustrated by Rudolf Schnackenburg’s "), _c('em', [_vm._v("Gottes Herrschaft und Reich")]), _vm._v(". Schnackenburg contrasts his “progressive-"), _c('em', [_vm._v("heilsgeschichtlich")]), _vm._v("” interpretation, which represents the newer Catholic view, with the older Catholic “church historical” view. The "), _c('em', [_vm._v("Gottesherrschaft")]), _vm._v(" is fundamentally eschatological and redemptive, but the eschatological salvation is present in the person and mission of Jesus in a real although provisional sense, in a hidden form. The best terms to describe the biblical view, Schnackenburg thinks, are promise, fulfillment, and consummation ("), _c('em', [_vm._v("Verheissung, Erfüllung, Vollendung")]), _vm._v(")."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_145",
      "id": "foot-145"
    }
  }, [_vm._v("145")]), _vm._v(" The consummation of the Kingdom remains future and eschatological; but the Kingdom of God is itself present in the time of messianic fulfillment in Christ."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_146",
      "id": "foot-146"
    }
  }, [_vm._v("146")])]), _vm._v(" The present Kingdom is "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("not to be identified with the church. The church is the people of the Kingdom, and the Kingdom works in and through the church as it did in and through Jesus. Schnackenburg admits the validity of what F. M. Braun called the “new consensus” among Protestant scholars as to the character of the Kingdom and its relationship to the church, differing from these Protestant theologians only as to the nature of the church, the primacy of Peter, and the question of succession."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_147",
      "id": "foot-147"
    }
  }, [_vm._v("147")]), _vm._v(" Schnackenburg carries his study beyond the Gospels through the preaching of the early church, Paul, and the later New Testament writings.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("One of the most important developments in German theology is Günther Bornkamm’s "), _c('em', [_vm._v("Jesus von Nazaret")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_148",
      "id": "foot-148"
    }
  }, [_vm._v("148")])]), _vm._v(" Famed as one of the most able and influential disciples of Bultmann, Bornkamm rejects Bultmann’s extreme historical skepticism and purposes in this book to portray the historical Jesus as advanced German scholarship has not dared to attempt for a generation. Bornkamm frequently follows Bultmann, but deviates at one crucial point. For Bultmann, that which created Jesus’ eschatological view was the overpowering sense of the presence of God. Bornkamm takes this consciousness of “unmediated presence” ("), _c('em', [_vm._v("unmittelbare Gegenwart")]), _vm._v(") to be the Kingdom of God as a present reality. Since "), _c('em', [_vm._v("basileia")]), _vm._v(" can be used synonymously with God himself,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_149",
      "id": "foot-149"
    }
  }, [_vm._v("149")])]), _vm._v(" the sense of God’s presence is itself the Kingdom of God. Thus the difference between John’s and Jesus’ proclamations, which are verbally the same, is the difference between the eleventh and the twelfth hours. Jesus announced that the turn of the ages is already present; the Kingdom of God is already dawning,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_150",
      "id": "foot-150"
    }
  }, [_vm._v("150")])]), _vm._v(" in his own words and deeds. Men are now confronted with the new age and therefore must render a decision."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_151",
      "id": "foot-151"
    }
  }, [_vm._v("151")])]), _vm._v(" In Jesus’ person the Kingdom has become event."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_152",
      "id": "foot-152"
    }
  }, [_vm._v("152")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_36"
    }
  }, [_vm._v("However, Bornkamm like Bultmann interprets the meaning of this event in existential terms. The “end of the world” does not mean an eschatological drama; it means rather that men are confronted by an event in which God becomes unmediated presence, and the result is “the end of the world”: before them stands either salvation or judgment. They sustain a new relationship to both their past and the future. In this sense, “time” has come to an end. Thus every man receives a new present; for the life, the world, and the existence of every man stand now in the unmediated illuminating light of the reality and presence of the coming God. The beginning of the Kingdom of God is an invisible event in this time and in this world, which brings time and the world to an end; for the new world of God is already at work."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_153",
      "id": "foot-153"
    }
  }, [_vm._v("153")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In 1950, Herman Ridderbos published a book on the Kingdom of God, which was translated from Dutch into English by H. de Jongste, entitled, "), _c('em', [_vm._v("The Coming of the Kingdom")]), _vm._v(" (1962). In it he defends the same basic outline which provides the structure of the present book. He understands the Kingdom of God to be the reign of God active in history, in fulfillment of the Old Testament hope in the historical mission of Jesus, which must come to eschatological consummation at the Parousia of Jesus.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Of great interest is the book by Norman Perrin, "), _c('em', [_vm._v("Rediscovering the Teaching of Jesus")]), _vm._v(" (1967), in which he deals at length with the Kingdom of God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_154",
      "id": "foot-154"
    }
  }, [_vm._v("154")])]), _vm._v(" It is notable especially for its use of radical form criticism in “rediscovering” the teaching of Jesus. Perrin insists upon “the criterion of dissimilarity”: i.e., “we may accept as authentic material which fits in with neither Jewish thinking nor the conceptions of the later [Christian] community.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_155",
      "id": "foot-155"
    }
  }, [_vm._v("155")])]), _vm._v(" This is to the present writer a highly arbitrary and hypercritical norm. It is utterly unreasonable "), _c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_vm._v("that Jesus’ teaching would not make use of contemporary Jewish idiom and thought, and it is equally unreasonable that the early church would not often have reproduced some of his teachings. This is surely an exercise in historical implausibility. We cannot here defend the integrity of the gospel portrait of Jesus; it is an involved question which involves dogmatic as well as “scientific” matters."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_156",
      "id": "foot-156"
    }
  }, [_vm._v("156")]), _vm._v(" We are here interested only in the conclusions to which Perrin’s radical criticism leads him.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Amazingly, his conclusions are remarkably like those of the present author. The “Kingdom of God” is first of all God’s dynamic activity and it is also the “state secured for the redeemed by this intervention” ("), _c('span', {
    attrs: {
      "href": "#/011_Chapter02#page_60",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 60")]), _vm._v("). It is also certain that Jesus proclaimed the eschatological Kingdom of God: God’s final and decisive act ("), _c('span', {
    attrs: {
      "href": "#/011_Chapter02#page_56",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 56")]), _vm._v("). Furthermore, Perrin sees the Kingdom as a present blessing (pp. 67, 74). We agree with Perrin also that because the Kingdom is present, it is something to be experienced. “Here was a situation in which the reality of God and his love was being revealed in a new and decisive way, and in which, therefore, the joys of the salvation time were suddenly available to those who had longed for them so long and so earnestly” ("), _c('span', {
    attrs: {
      "href": "#/012_Chapter03#page_97",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 97")]), _vm._v("). Table-fellowship, which frequently characterized Jesus’ ministry, “celebrated the present joy and anticipated the future consummation” ("), _c('span', {
    attrs: {
      "href": "#/014_Chapter04#page_107",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 107")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("At three important points, however, Perrin is in error. He is one of the few scholars who maintain that "), _c('em', [_vm._v("all")]), _vm._v(" of the Son of Man sayings, including the eschatological sayings, are church creations. In the present writer’s judgment, if criticism cannot establish that Jesus used the title “the Son of Man” of himself, it can prove very little."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_157",
      "id": "foot-157"
    }
  }, [_vm._v("157")])]), _vm._v(" Secondly, Perrin misreads the presence of the Kingdom of God. While Perrin rightly recognizes the presence of the Kingdom in an “eschatological-conflict” situation (Matt. 12:28), he insists that it is present in the experience of the individual (pp. 67, 74). Kümmel has recognize this error. “The presence of the Kingdom of God is divorced "), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("from the person of Jesus and limited to human experience.… The significance of the person of Jesus for this presence is almost completely lost in Perrin’s interpretation.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_158",
      "id": "foot-158"
    }
  }, [_vm._v("158")]), _vm._v(" We cannot emphasize this too strongly. We agree with Kümmel that the Kingdom of God came among men in the person, deeds, and words of Jesus. Only because of its presence in him could it be a matter of experience to his disciples.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Third, when Perrin tries to interpret the meaning of futurity for the Kingdom, he again falls into error. Many of his words, taken out of context, are splendidly expressed. Some of the parables involve a present beginning and a future consummation ("), _c('span', {
    attrs: {
      "href": "#/016_Chapter06#page_158",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 158")]), _vm._v("). “The emphasis is upon God, upon what he is doing and what he will do” ("), _c('span', {
    attrs: {
      "href": "#/016_Chapter06#page_158",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 158")]), _vm._v("). God was active in the ministry of Jesus, and “what God has begun he will bring to a triumphant conclusion” ("), _c('span', {
    attrs: {
      "href": "#/016_Chapter06#page_159",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 159")]), _vm._v("). But Perrin says that we are not to think of this future in terms of the “western concept of linear time” ("), _c('span', {
    attrs: {
      "href": "#/018_Chapter08#page_204",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 204")]), _vm._v(") which moves from past to present. Rather, “time in the teaching of Jesus” is “something which God fills and fulfills” ("), _c('span', {
    attrs: {
      "href": "#/018_Chapter08#page_206",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 206")]), _vm._v("). It is true that time gets its meaning from the fact that God is able to fill a moment or an hour of time; but this does not rob time of its linear meaning. Cullmann has proven that New Testament theology has its setting in the Jewish concept of linear time—this age and the age to come."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_159",
      "id": "foot-159"
    }
  }, [_vm._v("159")])]), _vm._v(" Again, Kümmel is right in saying that, at this point, Perrin is unhistorical and therefore false."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_160",
      "id": "foot-160"
    }
  }, [_vm._v("160")])]), _vm._v(" When Perrin says that “the present that has become God’s present guarantees that all futures will be God’s future” ("), _c('span', {
    attrs: {
      "href": "#/018_Chapter08#page_205",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("p. 205")]), _vm._v("), he ceases to be a biblical theologian.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A synthesis of present and future in the understanding of the Kingdom of God is found in the works of a host of other scholars dealing with other subjects. So extensively is this synthesis to be found"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_161",
      "id": "foot-161"
    }
  }, [_vm._v("161")])]), _vm._v(" that we must recognize it as an emerging consensus.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1"
  }, [_c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("C"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ONCLUSIONS")])])]), _c('a', {
    attrs: {
      "id": "page_39"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("From this critical survey, several conclusions emerge. First, the growing consensus is supported by the almost universal modification of Schweitzer’s Consistent Eschatology. Many of those who continue to deny that the Kingdom itself was present admit at least that it was active among men, that its powers were operative. The question of basic definition therefore becomes all-important. If the Kingdom is the future eschatological era of salvation, it is difficult to see how it can be also present. But if Otto was correct in defining the Kingdom ")]), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("not only as a future realm but as the power of God operative in that realm, then the activity of the power of the future Kingdom means, in reality, the presence of the Kingdom itself. The question of definition is fundamental.")])]), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("Second, the question of the imminence of the Kingdom has assumed new importance; for many of these recent studies, from Ernest F. Scott to W. G. Kümmel, find the present significance of the Kingdom in its imminence. The Kingdom is so "), _c('em', [_vm._v("very")]), _vm._v(" near that it can already be seen and felt. This fact of the imminence of the eschatological Kingdom, Wilder considers the central point of Schweitzer’s reconstruction,"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#foot_162",
      "id": "foot-162"
    }
  }, [_vm._v("162")]), _vm._v(" and he points out that few modern scholars challenge this conclusion. But at this point, a serious difficulty arises which, in the judgment of the present writer, has not been adequately faced. If it were a sense of imminence which caused our Lord’s feeling that the Kingdom was a present power, and if in fact the Kingdom was not imminent, then his teaching of the Kingdom as a present reality rested upon a delusion; and it is difficult to see how it can have validity either for him or for us. Some scholars have tried to solve this problem by translating his sense of imminence into an ethical or spiritual equivalent (William Manson, Rudolf Bultmann, W G. Kümmel). This stratagem tends to empty eschatology of real significance. Others, like Schweitzer, have admitted that the eschatology of Jesus is an offense to modern thought. Martin Dibelius says simply that Jesus was the victim of a monstrous illusion,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#foot_163",
      "id": "foot-163"
    }
  }, [_vm._v("163")])]), _vm._v(" for the very center of his message was an event which has never come to pass. Furthermore, if it were the sense of temporal imminence which gave Jesus a conviction that the powers of the Kingdom were already at work among men because the Kingdom itself was so close, and if in fact the Kingdom was not close, then the consciousness of these powers of the allegedly imminent Kingdom must itself have been illusory. The difficulty of this problem has not been fully admitted. If "), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("Jesus were in gross error as to his central mission, it is difficult to understand how his integrity or authority as a religious teacher can be preserved. If it is a fact that Jesus unequivocally thought that the Kingdom of God meant the end of the world in his lifetime, then we must not only admit that he was in error but must recognize that his entire message rested upon a delusion. We cannot take a Bultmannian position that the redemptive act occurs only in the Kerygma and needs only the fact "), _c('em', [_vm._v("(Dass)")]), _vm._v(" of Jesus to legitimate itself.")])]), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Third, the question of the role of apocalyptic is still contested. As we have seen, efforts have been made to distinguish between eschatology and apocalyptic and to attribute the former to Jesus, but not the latter. Schweitzer’s school interprets Jesus as a Jewish apocalyptist. Scholars like Goguel and Kümmel differentiate between apocalyptic speculation and eschatological instruction and make Jesus an adherent of the latter type of teaching. Others, like Bowman and Waterman, define prophetic and apocalyptic as mutually exclusive types of religion and place Jesus in the prophetic type while rejecting all apocalyptic. Still others, like Dodd and Wilder, insist that apocalyptic terminology is symbolic of eternal or ineffable realities and was not meant to be taken literally. These discussions raise the problem of the definition of prophetic and apocalyptic, and Jesus’ relation to both types of teaching; and this demands an analysis of the Old Testament promise and of its later apocalyptic interpretation as a background for the discussion of our Lord’s teachings.")])]), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("Finally, it becomes clear that the most important factor in the entire study is the point of departure and the fundamental definition of the Kingdom. If the Kingdom of God by definition is the eschatological realm of redemption, the age to come, and if Jesus proclaimed simply the imminence of this new age, it is difficult to see how the insuperable difficulties mentioned above can be avoided. On the other hand, if the Kingdom is primarily an experience of God in the human heart, a personal relationship between the individual and God, then perhaps the eschatological and apocalyptic elements have no real place in Jesus’ teaching and are to be sloughed off as views which he shared with his contemporary first-century Jewish kinsmen ")]), _c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_vm._v("but which lack relevance either for his real message or for the modern man. If, however, the Kingdom is the reign of God, not merely in the human heart but dynamically active in the person of Jesus and in human history, then it becomes possible to understand how the Kingdom of God can be present and future, inward and outward, spiritual and apocalyptic. For the redemptive royal activity of God could act decisively more than once and manifest itself powerfully in more than one way in accomplishing the divine end.")])]), _c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_c('p', {
    staticClass: "indent-para"
  }, [_vm._v("With these questions in mind, we must trace the prophetic expectation of the Kingdom of God in the Old Testament, pursue the developments of this expectation through the intertestamental literature, and then against this background attempt a reconstruction of the teaching and mission of our Lord with particular reference to the Kingdom of God.")]), _vm._v(" "), _c('p', {
    staticClass: "imagef"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/foot.jpg"
    }
  })])]), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }), _c('a', {
    attrs: {
      "href": "#/#foot-1",
      "id": "foot_1"
    }
  }, [_vm._v("1")]), _vm._v(". A. M. Hunter, "), _c('em', [_vm._v("Interpreting the New Testament, 1900–1950")]), _vm._v(" (1951), p. 136.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-2",
      "id": "foot_2"
    }
  }, [_vm._v("2")]), _vm._v(". For other surveys see R. N. Flew, "), _c('em', [_vm._v("ET")]), _vm._v(", XLVI (1934/5), pp. 214–218; C. C. McCown, "), _c('em', [_vm._v("JR")]), _vm._v(", XVI (1936), pp. 30–46; A. N. Wilder, "), _c('em', [_vm._v("JR")]), _vm._v(", XXVIII (1948), pp. 177–187; "), _c('em', [_vm._v("Eschatology and Ethics in the Teaching of Jesus")]), _vm._v(" (1950), Chap. 2; H. Roberts, "), _c('em', [_vm._v("Jesus and the Kingdom of God")]), _vm._v(" (1955), pp. 9–20; G. Lundström, "), _c('em', [_vm._v("The Kingdom of God in the Teaching of Jesus")]), _vm._v(" (1963); N. Perrin, "), _c('em', [_vm._v("The Kingdom of God in the Teaching of Jesus")]), _vm._v(" (1963).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-3",
      "id": "foot_3"
    }
  }, [_vm._v("3")]), _vm._v(". "), _c('em', [_vm._v("What Is Christianity?")]), _vm._v(" (1901), pp. 21–83.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-4",
      "id": "foot_4"
    }
  }, [_vm._v("4")]), _vm._v(". "), _c('em', [_vm._v("Israel and the New Covenant")]), _vm._v(" (1954). For a survey of the older conservatives, see G. E. Ladd, "), _c('em', [_vm._v("Crucial Questions about the Kingdom of God")]), _vm._v(" (1952), pp. 27 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-5",
      "id": "foot_5"
    }
  }, [_vm._v("5")]), _vm._v(". "), _c('em', [_vm._v("Paul and His Interpreters")]), _vm._v(" (1912), p. ix. See the penetrating observations by H. G. Wood, "), _c('em', [_vm._v("Jesus in the Twentieth Century")]), _vm._v(" (1960), p. 172. See also G. Lundström, "), _c('em', [_vm._v("The Kingdom of God in the Teaching of Jesus")]), _vm._v(" (1963), p. 93.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-6",
      "id": "foot_6"
    }
  }, [_vm._v("6")]), _vm._v(". A. Schweitzer, "), _c('em', [_vm._v("The Quest of the Historical Jesus")]), _vm._v(" (1911), p. 399. See also J. Weiss, "), _c('em', [_vm._v("Die Predigt Jesu vom Reiche Gottes")]), _vm._v(" (1892; 2nd enlarged edition, 1900). The first edition of this book has now been translated by R. H. Hiers and D. L. Holland, "), _c('em', [_vm._v("Jesus’ Proclamation of the Kingdom of God")]), _vm._v(" (1971). See also R. H. Hiers, "), _c('em', [_vm._v("The Kingdom of God in the Synoptic Tradition")]), _vm._v(" (1970), who follows Weiss’ view.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-7",
      "id": "foot_7"
    }
  }, [_vm._v("7")]), _vm._v(". "), _c('em', [_vm._v("The Life of Jesus")]), _vm._v(" (1933), Eng. trans., pp. 569–572.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-8",
      "id": "foot_8"
    }
  }, [_vm._v("8")]), _vm._v(". M. Goguel, "), _c('em', [_vm._v("Revue d’Histoire des Religions")]), _vm._v(", CVI (1932), pp. 382 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-9",
      "id": "foot_9"
    }
  }, [_vm._v("9")]), _vm._v(". "), _c('em', [_vm._v("Jesus")]), _vm._v(" (1935), p. 341.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-10",
      "id": "foot_10"
    }
  }, [_vm._v("10")]), _vm._v(". "), _c('em', [_vm._v("The Formation of Christian Dogma")]), _vm._v(" (1957), pp. 9–27.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-11",
      "id": "foot_11"
    }
  }, [_vm._v("11")]), _vm._v(". "), _c('em', [_vm._v("Jesus")]), _vm._v(" (1949).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-12",
      "id": "foot_12"
    }
  }, [_vm._v("12")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 69–88. See the similar statement by Erich Dinkier in "), _c('em', [_vm._v("The Idea of History in the Ancient Near East")]), _vm._v(" (R. C. Dentan, ed.; 1955), pp. 173–180.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-13",
      "id": "foot_13"
    }
  }, [_vm._v("13")]), _vm._v(". "), _c('em', [_vm._v("Theology of the New Testament")]), _vm._v(" (1951), I. p. 22. Bultmann has apparently wavered in this conviction, for in one place, he admits that Jesus saw the “turn of the ages” in the appearance of the Baptist and understood himself to stand in the interim “between the times.” See “Man between the Times” in "), _c('em', [_vm._v("Existence and Faith: Shorter Writings of Rudolf Bultmann")]), _vm._v(" (Schubert M. Ogden, ed.; 1960), p. 253.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-14",
      "id": "foot_14"
    }
  }, [_vm._v("14")]), _vm._v(". It will be noted below that Bultmann does not derive his interpretation from purely objective exegesis but grounds Jesus’ view of the future on existential presuppositions. See below, "), _c('a', {
    attrs: {
      "href": "#/009_Chapter01#page_20"
    }
  }, [_vm._v("pp. 20 ff")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-15",
      "id": "foot_15"
    }
  }, [_vm._v("15")]), _vm._v(". Grässer, "), _c('em', [_vm._v("Das Problem der Parusieverzögerung in den synoptischen Evangelien und in der Apostlegeschichte")]), _vm._v(" (1957).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-16",
      "id": "foot_16"
    }
  }, [_vm._v("16")]), _vm._v(". "), _c('em', [_vm._v("The Theology of Saint Luke")]), _vm._v(" (1960). See also Conzelmann’s articles on “Reich Gottes” in "), _c('em', [_vm._v("RGG")]), _vm._v(", 3rd ed., V, col. 912–18; “Jesus Christus,” "), _c('em', [_vm._v("ibid.")]), _vm._v(", III, col. 641–46. For the entire discussion, see O. Cullmann, in "), _c('em', [_vm._v("TLZ")]), _vm._v(", LXXXIII (1958), col. 1–12.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-17",
      "id": "foot_17"
    }
  }, [_vm._v("17")]), _vm._v(". See his essay in "), _c('em', [_vm._v("A History of Christianity in the Light of Modern Knowledge")]), _vm._v(" (1929), p. 234.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-18",
      "id": "foot_18"
    }
  }, [_vm._v("18")]), _vm._v(". See "), _c('em', [_vm._v("The Life of Christ in Recent Research")]), _vm._v(" (1907), p. 121; but see his essay, “The Apocalyptic Element in the Gospels,” "), _c('em', [_vm._v("Hibbert Journal")]), _vm._v(", X (1911–12), pp. 83–109.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-19",
      "id": "foot_19"
    }
  }, [_vm._v("19")]), _vm._v(". "), _c('em', [_vm._v("The Historical Life of Christ")]), _vm._v(" (1926).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-20",
      "id": "foot_20"
    }
  }, [_vm._v("20")]), _vm._v(". "), _c('em', [_vm._v("The Kingdom and the Messiah")]), _vm._v(" (1911). In a later eclectic study, "), _c('em', [_vm._v("The Kingdom of God in the New Testament")]), _vm._v(" (1931), Scott modified his earlier position and defined the Kingdom as the will of God, which is broad enough to include all the meanings which men have found in it (p. 194). The present reign of God is a higher spiritual and moral order, a present inner life, and a social ideal. The framework of apocalyptic to us is fantastic and fanatical; but it contains the inherent religious value that it is God who acts through crisis (pp. 117 f., 188).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-21",
      "id": "foot_21"
    }
  }, [_vm._v("21")]), _vm._v(". B. S. Easton, "), _c('em', [_vm._v("Christ in the Gospels")]), _vm._v(" (1930), pp. 158–164.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-22",
      "id": "foot_22"
    }
  }, [_vm._v("22")]), _vm._v(". Morton Scott Enslin, "), _c('em', [_vm._v("The Prophet from Nazareth")]), _vm._v(" (1961), p. 72. Enslin characterizes efforts to “soften and alter” the purely futuristic eschatological character of Jesus’ teaching as “frivolous.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-23",
      "id": "foot_23"
    }
  }, [_vm._v("23")]), _vm._v(". S. J. Case, "), _c('em', [_vm._v("Jesus")]), _vm._v(" (1927), pp. 436 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-24",
      "id": "foot_24"
    }
  }, [_vm._v("24")]), _vm._v(". D. W. Riddle and H. H. Hutson, "), _c('em', [_vm._v("New Testament Life and Literature")]), _vm._v(" (1946), pp. 90 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-25",
      "id": "foot_25"
    }
  }, [_vm._v("25")]), _vm._v(". C. T. Craig, "), _c('em', [_vm._v("The Beginning of Christianity")]), _vm._v(" (1943), p. 87; "), _c('em', [_vm._v("IB")]), _vm._v(", VII, p. 148.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-26",
      "id": "foot_26"
    }
  }, [_vm._v("26")]), _vm._v(". R. H. Fuller, "), _c('em', [_vm._v("The Mission and Achievement of Jesus")]), _vm._v(" (1954), pp. 25, 26, 32. Quoted by permission, London, S. C. M. Press. Distributed in U.S.A. by Allenson’s, Naperville, Illinois.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-27",
      "id": "foot_27"
    }
  }, [_vm._v("27")]), _vm._v(". Alan Richardson considers Fuller’s criticism of Realized Eschatology to be convincing. See "), _c('em', [_vm._v("An Introduction to the Theology of the New Testament")]), _vm._v(" (1958), p. 86, n. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-28",
      "id": "foot_28"
    }
  }, [_vm._v("28")]), _vm._v(". 1908; pp. 312–320.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-29",
      "id": "foot_29"
    }
  }, [_vm._v("29")]), _vm._v(". E. von Dobschütz, "), _c('em', [_vm._v("The Eschatology of the Gospels")]), _vm._v(" (1910), p. 150.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-30",
      "id": "foot_30"
    }
  }, [_vm._v("30")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 143.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-31",
      "id": "foot_31"
    }
  }, [_vm._v("31")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 207.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-32",
      "id": "foot_32"
    }
  }, [_vm._v("32")]), _vm._v(". T. W. Manson, "), _c('em', [_vm._v("The Teaching of Jesus")]), _vm._v(" (1935), p. 135.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-33",
      "id": "foot_33"
    }
  }, [_vm._v("33")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 211.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-34",
      "id": "foot_34"
    }
  }, [_vm._v("34")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 129.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-35",
      "id": "foot_35"
    }
  }, [_vm._v("35")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 130.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-36",
      "id": "foot_36"
    }
  }, [_vm._v("36")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 133.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-37",
      "id": "foot_37"
    }
  }, [_vm._v("37")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 134.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-38",
      "id": "foot_38"
    }
  }, [_vm._v("38")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 284. Quoted by permission of Cambridge University Press.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-39",
      "id": "foot_39"
    }
  }, [_vm._v("39")]), _vm._v(". E. Percy, "), _c('em', [_vm._v("Die Botschaft Jesu")]), _vm._v(" (1953), p. 224.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-40",
      "id": "foot_40"
    }
  }, [_vm._v("40")]), _vm._v(". T. W. Manson, "), _c('em', [_vm._v("The Teaching of Jesus")]), _vm._v(" (1935), p. 190. In a subsequent book Manson gave far more adequate recognition to eschatology. See below, "), _c('a', {
    attrs: {
      "href": "#/009_Chapter01#page_28"
    }
  }, [_vm._v("p. 28")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-41",
      "id": "foot_41"
    }
  }, [_vm._v("41")]), _vm._v(". C. J. Cadoux, "), _c('em', [_vm._v("The Historic Mission of Jesus")]), _vm._v(" (n.d. [ca. 1940]), p. 133.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-42",
      "id": "foot_42"
    }
  }, [_vm._v("42")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 115.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-43",
      "id": "foot_43"
    }
  }, [_vm._v("43")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 204.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-44",
      "id": "foot_44"
    }
  }, [_vm._v("44")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 206.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-45",
      "id": "foot_45"
    }
  }, [_vm._v("45")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 26, 129.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-46",
      "id": "foot_46"
    }
  }, [_vm._v("46")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 103.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-47",
      "id": "foot_47"
    }
  }, [_vm._v("47")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 173.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-48",
      "id": "foot_48"
    }
  }, [_vm._v("48")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 218.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-49",
      "id": "foot_49"
    }
  }, [_vm._v("49")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 340 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-50",
      "id": "foot_50"
    }
  }, [_vm._v("50")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 348.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-51",
      "id": "foot_51"
    }
  }, [_vm._v("51")]), _vm._v(". "), _c('em', [_vm._v("The Gospel of the Kingdom")]), _vm._v(" (1940). See also “The Idea of the Kingdom of God in the New Testament,” in "), _c('em', [_vm._v("The Sacral Kingship")]), _vm._v(" (1959), pp. 437–446; "), _c('em', [_vm._v("Ancient Judaism and the New Testament")]), _vm._v(" (1959), Chap. 8.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-52",
      "id": "foot_52"
    }
  }, [_vm._v("52")]), _vm._v(". H. B. Sharman, "), _c('em', [_vm._v("The Teaching of Jesus about the Future")]), _vm._v(" (1909), pp. 301–327. Cf. also Sharman’s "), _c('em', [_vm._v("Son of Man and Kingdom of God")]), _vm._v(" (1943).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-53",
      "id": "foot_53"
    }
  }, [_vm._v("53")]), _vm._v(". "), _c('em', [_vm._v("Jesus in the Light of History")]), _vm._v(" (1942), p. xi.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-54",
      "id": "foot_54"
    }
  }, [_vm._v("54")]), _vm._v(". See "), _c('em', [_vm._v("ThTo")]), _vm._v(", XI (1954), pp. 173 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-55",
      "id": "foot_55"
    }
  }, [_vm._v("55")]), _vm._v(". J. W. Bowman, "), _c('em', [_vm._v("Prophetic Realism and the Gospel")]), _vm._v(" (1955), pp. 34–37.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-56",
      "id": "foot_56"
    }
  }, [_vm._v("56")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 200–202.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-57",
      "id": "foot_57"
    }
  }, [_vm._v("57")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 207.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-58",
      "id": "foot_58"
    }
  }, [_vm._v("58")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 210.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-59",
      "id": "foot_59"
    }
  }, [_vm._v("59")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 222–225.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-60",
      "id": "foot_60"
    }
  }, [_vm._v("60")]), _vm._v(". J. W. Bowman, "), _c('em', [_vm._v("The Religion of Maturity")]), _vm._v(" (1948), pp. 228, 248. See G. E. Ladd, “Why Not Prophetic-Apocalyptic?” "), _c('em', [_vm._v("JBL")]), _vm._v(", LXXVI (1957), pp. 192–200.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-61",
      "id": "foot_61"
    }
  }, [_vm._v("61")]), _vm._v(". (1943), pp. 148, 153.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-62",
      "id": "foot_62"
    }
  }, [_vm._v("62")]), _vm._v(". J. W. Bowman, "), _c('em', [_vm._v("The Religion of Maturity")]), _vm._v(" (1948), p. 240.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-63",
      "id": "foot_63"
    }
  }, [_vm._v("63")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 310.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-64",
      "id": "foot_64"
    }
  }, [_vm._v("64")]), _vm._v(". In recent articles, Bowman admits a residuum of futuristic eschatology in Jesus’ teaching. See “Eschatology of the New Testament,” "), _c('em', [_vm._v("IDB")]), _vm._v(", II, p. 137; "), _c('em', [_vm._v("Peake’s Commentary on the Bible")]), _vm._v(" (M. Black and H. H. Rowley, eds.; 1962), pp. 739, 745. It is difficult to know how this fits into Bowman’s total interpretation.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-65",
      "id": "foot_65"
    }
  }, [_vm._v("65")]), _vm._v(". C. H. Dodd, "), _c('em', [_vm._v("The Parables of the Kingdom")]), _vm._v(" (1936); "), _c('em', [_vm._v("The Apostolic Preaching and Its Developments")]), _vm._v(" (1936).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-66",
      "id": "foot_66"
    }
  }, [_vm._v("66")]), _vm._v(". "), _c('em', [_vm._v("The Parables of the Kingdom")]), _vm._v(" (1936), p. 49.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-67",
      "id": "foot_67"
    }
  }, [_vm._v("67")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 100 f. For the debate over "), _c('em', [_vm._v("eggiken")]), _vm._v(" and "), _c('em', [_vm._v("ephthasen")]), _vm._v(", see C. T. Craig, "), _c('em', [_vm._v("JBL")]), _vm._v(", LVI (1937), pp. 17–26; J. Y. Campbell, ET, XLVIII (1936–37), pp. 91–94; K. Clark, "), _c('em', [_vm._v("JBL")]), _vm._v(", LIX (1940), pp. 367–383. See also E. E. Wolfzorn, "), _c('em', [_vm._v("Ephemerides Theologicae Lovaniensis")]), _vm._v(", XXXVIII (1962), pp. 54–55.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-68",
      "id": "foot_68"
    }
  }, [_vm._v("68")]), _vm._v(". C. H. Dodd, "), _c('em', [_vm._v("The Apostolic Preaching and Its Developments")]), _vm._v(" (1936), pp. 145, 174.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-69",
      "id": "foot_69"
    }
  }, [_vm._v("69")]), _vm._v(". "), _c('em', [_vm._v("The Parables of the Kingdom")]), _vm._v(" (1936), p. 106.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-70",
      "id": "foot_70"
    }
  }, [_vm._v("70")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 56.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-71",
      "id": "foot_71"
    }
  }, [_vm._v("71")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 197.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-72",
      "id": "foot_72"
    }
  }, [_vm._v("72")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 207.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-73",
      "id": "foot_73"
    }
  }, [_vm._v("73")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 107.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-74",
      "id": "foot_74"
    }
  }, [_vm._v("74")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 107 f. Quoted by permission of James Nisbet Co.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-75",
      "id": "foot_75"
    }
  }, [_vm._v("75")]), _vm._v(". J. Jeremias, "), _c('em', [_vm._v("The Parables of Jesus")]), _vm._v(" (1954), pp. 18 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-76",
      "id": "foot_76"
    }
  }, [_vm._v("76")]), _vm._v(". Ibid., p. 159 ("), _c('em', [_vm._v("Die Gleichnisse Jesu")]), _vm._v(" [1947], p. 114, “sich realisierende Eschatologie”).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-77",
      "id": "foot_77"
    }
  }, [_vm._v("77")]), _vm._v(". C. H. Dodd, "), _c('em', [_vm._v("The Interpretation of the Fourth Gospel")]), _vm._v(" (1953), p. 447.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-78",
      "id": "foot_78"
    }
  }, [_vm._v("78")]), _vm._v(". C. H. Dodd, "), _c('em', [_vm._v("The Coming of Christ")]), _vm._v(" (1951), p. 38.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-79",
      "id": "foot_79"
    }
  }, [_vm._v("79")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 26. For a defense of Dodd, see Claude H. Thompson, "), _c('em', [_vm._v("Theology of the Kerygma")]), _vm._v(" (1962), pp. 10–13.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-80",
      "id": "foot_80"
    }
  }, [_vm._v("80")]), _vm._v(". R. Bultmann, "), _c('em', [_vm._v("Theology of the New Testament")]), _vm._v(" (1951), I, p. 23.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-81",
      "id": "foot_81"
    }
  }, [_vm._v("81")]), _vm._v(". R. Bultmann, "), _c('em', [_vm._v("Jesus and the Word")]), _vm._v(" (1934), p. 41.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-82",
      "id": "foot_82"
    }
  }, [_vm._v("82")]), _vm._v(". F. C. Grant, "), _c('em', [_vm._v("An Introduction to New Testament Thought")]), _vm._v(" (1950), p. 206.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-83",
      "id": "foot_83"
    }
  }, [_vm._v("83")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 50.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-84",
      "id": "foot_84"
    }
  }, [_vm._v("84")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 185.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-85",
      "id": "foot_85"
    }
  }, [_vm._v("85")]), _vm._v(". J. Knox, "), _c('em', [_vm._v("Christ the Lord")]), _vm._v(" (1945), p. 30.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-86",
      "id": "foot_86"
    }
  }, [_vm._v("86")]), _vm._v(". W. Manson, "), _c('em', [_vm._v("Jesus, the Messiah")]), _vm._v(" (1946), p. 209. Copyright, 1946, by The Westminster Press. Used by permission.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-87",
      "id": "foot_87"
    }
  }, [_vm._v("87")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 208. In an earlier book, "), _c('em', [_vm._v("Christ’s View of the Kingdom of God")]), _vm._v(" (1918), W. Manson had embraced an evolutionary interpretation which he later discarded. In one of his latest statements, we read, “There is a realized eschatology. There is also an eschatology of the unrealized. There can be no such thing under any imaginable conditions as a fully realized eschatology in the strict sense.” (W. Manson, "), _c('em', [_vm._v("et al")]), _vm._v("., "), _c('em', [_vm._v("Eschatology")]), _vm._v(" [n.d.], p. 7.)")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-88",
      "id": "foot_88"
    }
  }, [_vm._v("88")]), _vm._v(". T. W. Manson, "), _c('em', [_vm._v("The Servant-Messiah")]), _vm._v(" (1953), p. 63.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-89",
      "id": "foot_89"
    }
  }, [_vm._v("89")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", P. 74.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-90",
      "id": "foot_90"
    }
  }, [_vm._v("90")]), _vm._v(". "), _c('em', [_vm._v("Das Reich Gottes im Neuen Testament")]), _vm._v(" (1928); "), _c('em', [_vm._v("Reich Gottes und Kirche im Neuen Testament")]), _vm._v(" (1929). See the review by R. N. Flew in "), _c('em', [_vm._v("ET")]), _vm._v(", XLVI (1935), pp. 215 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-91",
      "id": "foot_91"
    }
  }, [_vm._v("91")]), _vm._v(". H. D. Wendland, "), _c('em', [_vm._v("Die Eschatologie des Reiches Gottes bei Jesus")]), _vm._v(" (1931), pp. 27–53. See his essay in "), _c('em', [_vm._v("The Kingdom of God and History")]), _vm._v(" (H. G. Wood, ed.; 1938), pp. 145–194.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-92",
      "id": "foot_92"
    }
  }, [_vm._v("92")]), _vm._v(". H. D. Wendland, "), _c('em', [_vm._v("Eschatologie")]), _vm._v(", p. 45.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-93",
      "id": "foot_93"
    }
  }, [_vm._v("93")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 50.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-94",
      "id": "foot_94"
    }
  }, [_vm._v("94")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 46.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-95",
      "id": "foot_95"
    }
  }, [_vm._v("95")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 51 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-96",
      "id": "foot_96"
    }
  }, [_vm._v("96")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 41.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-97",
      "id": "foot_97"
    }
  }, [_vm._v("97")]), _vm._v(". R. Otto, "), _c('em', [_vm._v("The Kingdom of God and the Son of Man")]), _vm._v(" (1943), p. 105.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-98",
      "id": "foot_98"
    }
  }, [_vm._v("98")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 103.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-99",
      "id": "foot_99"
    }
  }, [_vm._v("99")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 55.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-100",
      "id": "foot_100"
    }
  }, [_vm._v("100")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 98.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-101",
      "id": "foot_101"
    }
  }, [_vm._v("101")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 72.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-102",
      "id": "foot_102"
    }
  }, [_vm._v("102")]), _vm._v(". J. Jeremias, "), _c('em', [_vm._v("Jesus als Weltvollender")]), _vm._v(" ("), _c('em', [_vm._v("Beiträge zur Forderung christlicher Theologie")]), _vm._v(", XXVII, 1930).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-103",
      "id": "foot_103"
    }
  }, [_vm._v("103")]), _vm._v(". J. Jeremias, "), _c('em', [_vm._v("The Parables of Jesus")]), _vm._v(" (1954), p. 158.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-104",
      "id": "foot_104"
    }
  }, [_vm._v("104")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 32 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-105",
      "id": "foot_105"
    }
  }, [_vm._v("105")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 44.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-106",
      "id": "foot_106"
    }
  }, [_vm._v("106")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 120–126.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-107",
      "id": "foot_107"
    }
  }, [_vm._v("107")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 122.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-108",
      "id": "foot_108"
    }
  }, [_vm._v("108")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 152 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-109",
      "id": "foot_109"
    }
  }, [_vm._v("109")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 153. In view of the fact that Jeremias asserts that it is unthinkable that there should be any retreat from the essential lines laid down by Dodd for the interpretation of the parables (p. 109) and that he criticizes Dodd only for his “contraction of the eschatology” and “one-sided nature of his conception of the Kingdom” (p. 18), it is difficult to accept Lundström’s conclusion that Jeremias has a purely futuristic view of the Kingdom of God (G. Lundström, "), _c('em', [_vm._v("The Kingdom of God in the Teaching of Jesus")]), _vm._v(" [1963], pp. 245–249. See the treatment of Jeremias by one of his own students, N. Perrin, "), _c('em', [_vm._v("The Kingdom of God in the Teaching of Jesus")]), _vm._v(" [1963], pp. 81–82). However, Lundström has pointed out a real difficulty in Jeremias’ view. In spite of statements about the presence of the Kingdom, his view can be interpreted as a process, initiated by Jesus’ mission, which will be shortly consummated. The Kingdom itself comes only at the consummation, it is not really present in the process already initiated.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_vm._v("In his most recent book, "), _c('em', [_vm._v("New Testament Theology")]), _vm._v(", vol. I (1971), Jeremias takes the rather astounding position that the disciples’ experience of the resurrection of Jesus was “the dawn of the eschaton. They saw Jesus in shining light. They were witnesses of his entry into glory. In other words, "), _c('em', [_vm._v("they experienced the parousia")]), _vm._v("” (italics in text; p. 310). Apparently there remains no realistic or futuristic eschatology.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-110",
      "id": "foot_110"
    }
  }, [_vm._v("110")]), _vm._v(". See H. D. Major, T. W. Manson, and C. J. Wright, "), _c('em', [_vm._v("The Mission and Message of Jesus")]), _vm._v(" (1938). Manson’s part of this work was republished under the title "), _c('em', [_vm._v("The Sayings of Jesus")]), _vm._v(" (1949). Our references are to the latter volume, for the preface indicates that this work represents Manson’s views in 1949.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-111",
      "id": "foot_111"
    }
  }, [_vm._v("111")]), _vm._v(". T. W. Manson, "), _c('em', [_vm._v("The Sayings of Jesus")]), _vm._v(" (1949), p. 345.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-112",
      "id": "foot_112"
    }
  }, [_vm._v("112")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 134.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-113",
      "id": "foot_113"
    }
  }, [_vm._v("113")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")])]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-114",
      "id": "foot_114"
    }
  }, [_vm._v("114")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 304.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-115",
      "id": "foot_115"
    }
  }, [_vm._v("115")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 305.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-116",
      "id": "foot_116"
    }
  }, [_vm._v("116")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 148.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-117",
      "id": "foot_117"
    }
  }, [_vm._v("117")]), _vm._v(". J. Héring, "), _c('em', [_vm._v("Le Royaume de Dieu et sa")]), _vm._v(" Venue (1937), pp. 48, 254. Lundström interprets Héring’s view as being purely futuristic. See G. Lundström, "), _c('em', [_vm._v("The Kingdom of God in the Teaching of Jesus")]), _vm._v(" (1963), p. 92.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-118",
      "id": "foot_118"
    }
  }, [_vm._v("118")]), _vm._v(". W. G. Kümmel, "), _c('em', [_vm._v("Promise and Fulfilment")]), _vm._v(" (1957), p. 105.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-119",
      "id": "foot_119"
    }
  }, [_vm._v("119")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 107 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-120",
      "id": "foot_120"
    }
  }, [_vm._v("120")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 126.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-121",
      "id": "foot_121"
    }
  }, [_vm._v("121")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 139 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-122",
      "id": "foot_122"
    }
  }, [_vm._v("122")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 95.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-123",
      "id": "foot_123"
    }
  }, [_vm._v("123")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 151.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-124",
      "id": "foot_124"
    }
  }, [_vm._v("124")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 91.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-125",
      "id": "foot_125"
    }
  }, [_vm._v("125")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 152.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-126",
      "id": "foot_126"
    }
  }, [_vm._v("126")]), _vm._v(". W. G. Kümmel, "), _c('em', [_vm._v("Promise and Fulfilment")]), _vm._v(" (1957), pp. 145 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-127",
      "id": "foot_127"
    }
  }, [_vm._v("127")]), _vm._v(". A. N. Wilder, "), _c('em', [_vm._v("Eschatology and Ethics in the Teaching of Jesus")]), _vm._v(" (1950), p. 59.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-128",
      "id": "foot_128"
    }
  }, [_vm._v("128")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 26 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-129",
      "id": "foot_129"
    }
  }, [_vm._v("129")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 60.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-130",
      "id": "foot_130"
    }
  }, [_vm._v("130")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 70.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-131",
      "id": "foot_131"
    }
  }, [_vm._v("131")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 161.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-132",
      "id": "foot_132"
    }
  }, [_vm._v("132")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 141.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-133",
      "id": "foot_133"
    }
  }, [_vm._v("133")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 61.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-134",
      "id": "foot_134"
    }
  }, [_vm._v("134")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 63.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-135",
      "id": "foot_135"
    }
  }, [_vm._v("135")]), _vm._v(". "), _c('em', [_vm._v("Theologie der Auferstehung")]), _vm._v(" (1934).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-136",
      "id": "foot_136"
    }
  }, [_vm._v("136")]), _vm._v(". Cited by A. N. Wilder, "), _c('em', [_vm._v("Eschatology and Ethics in the Teaching of Jesus")]), _vm._v(" (1950), p. 63.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-137",
      "id": "foot_137"
    }
  }, [_vm._v("137")]), _vm._v(". A. N. Wilder in "), _c('em', [_vm._v("NTS")]), _vm._v(", V (1959), pp. 229–245.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-138",
      "id": "foot_138"
    }
  }, [_vm._v("138")]), _vm._v(". A. N. Wilder, "), _c('em', [_vm._v("Eschatology and Ethics in the Teaching of Jesus")]), _vm._v(" (1950), p. 161.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-139",
      "id": "foot_139"
    }
  }, [_vm._v("139")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 21, 26.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-140",
      "id": "foot_140"
    }
  }, [_vm._v("140")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 51 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-141",
      "id": "foot_141"
    }
  }, [_vm._v("141")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 50 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-142",
      "id": "foot_142"
    }
  }, [_vm._v("142")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 147 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-143",
      "id": "foot_143"
    }
  }, [_vm._v("143")]), _vm._v(". See "), _c('em', [_vm._v("New Testament Faith for Today")]), _vm._v(" (1955), pp. 75 f. where the presence of the Kingdom is interpreted as its imminence which made its powers felt.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-144",
      "id": "foot_144"
    }
  }, [_vm._v("144")]), _vm._v(". A. M. Hunter, "), _c('em', [_vm._v("Introducing New Testament Theology")]), _vm._v(" (1957), pp. 13–51. See also "), _c('em', [_vm._v("The Work and Words of Jesus")]), _vm._v(" (1950), pp. 67–79, and "), _c('em', [_vm._v("Interpreting the Parables")]), _vm._v(" (1960). In these two books Hunter “plays down” the eschatological element even more than in his "), _c('em', [_vm._v("Theology")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-145",
      "id": "foot_145"
    }
  }, [_vm._v("145")]), _vm._v(". The structure of the present book had been planned long before Schnackenburg’s volume came to hand. See this threefold concept in N. Q. Hamilton, "), _c('em', [_vm._v("Int")]), _vm._v(", XIV (1960), pp. 141 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-146",
      "id": "foot_146"
    }
  }, [_vm._v("146")]), _vm._v(". R. Schnackenburg, "), _c('em', [_vm._v("God’s Rule and Kingdom")]), _vm._v(" (1963), pp. 116, 128. See also his review of the first edition of the present book in "), _c('em', [_vm._v("Biblische Zeitschrift")]), _vm._v(" (Spring, 1967), pp. 141–143.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-147",
      "id": "foot_147"
    }
  }, [_vm._v("147")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 161.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-148",
      "id": "foot_148"
    }
  }, [_vm._v("148")]), _vm._v(". 1956; Eng. trans., "), _c('em', [_vm._v("Jesus of Nazareth")]), _vm._v(" (1960).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-149",
      "id": "foot_149"
    }
  }, [_vm._v("149")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 200.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-150",
      "id": "foot_150"
    }
  }, [_vm._v("150")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 67.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-151",
      "id": "foot_151"
    }
  }, [_vm._v("151")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 68.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-152",
      "id": "foot_152"
    }
  }, [_vm._v("152")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", pp. 169 f.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-153",
      "id": "foot_153"
    }
  }, [_vm._v("153")]), _vm._v(". "), _c('em', [_vm._v("Ibid.")]), _vm._v(", p. 74.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-154",
      "id": "foot_154"
    }
  }, [_vm._v("154")]), _vm._v(". He had earlier published his doctoral dissertation, "), _c('em', [_vm._v("The Kingdom of God in the Teaching of Jesus")]), _vm._v(" (1963)—a book which, incidentally, is wrongly titled, for it is a history of interpretation, not primarily what the title suggests. For the present writer’s critique of this book see "), _c('em', [_vm._v("The Pattern of New Testament Truth")]), _vm._v(" (1968), pp. 58–63.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-155",
      "id": "foot_155"
    }
  }, [_vm._v("155")]), _vm._v(". "), _c('em', [_vm._v("Rediscovering the Teaching of Jesus")]), _vm._v(", p. 43.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-156",
      "id": "foot_156"
    }
  }, [_vm._v("156")]), _vm._v(". See G. E. Ladd, “A Search for Perspective,” "), _c('em', [_vm._v("Int")]), _vm._v(", XXV (1971), pp. 41–62.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-157",
      "id": "foot_157"
    }
  }, [_vm._v("157")]), _vm._v(". See Chap. 11 in the author’s forthcoming, "), _c('em', [_vm._v("A Theology of the New Testament")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-158",
      "id": "foot_158"
    }
  }, [_vm._v("158")]), _vm._v(". See W. G. Kümmel’s review in "), _c('em', [_vm._v("JR")]), _vm._v(", XLIX (1969), p. 62.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-159",
      "id": "foot_159"
    }
  }, [_vm._v("159")]), _vm._v(". O. Cullmann, "), _c('em', [_vm._v("Christ and Time")]), _vm._v(" (rev. ed. 1964).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-160",
      "id": "foot_160"
    }
  }, [_vm._v("160")]), _vm._v(". W. G. Kümmel, "), _c('em', [_vm._v("op. cit.")]), _vm._v(", p. 65.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-161",
      "id": "foot_161"
    }
  }, [_vm._v("161")]), _vm._v(". See J. A. Baird, "), _c('em', [_vm._v("The Justice of God in the Teaching of Jesus")]), _vm._v(" (1963), pp. 122 ff. (Baird thinks that the future of the discussion lies with the position which combines present and future); C. K. Barrett, “New Testament Eschatology,” "), _c('em', [_vm._v("SJTh")]), _vm._v(", VI (1953), p. 231; H. Bietenhard, "), _c('em', [_vm._v("Die Reichgottes-Erwartung im Neuen Testament")]), _vm._v(" (1945); J. Bright, "), _c('em', [_vm._v("The Kingdom of God")]), _vm._v(" (1953), Chap. 8; F. F. Bruce, “Eschatology,” in "), _c('em', [_vm._v("Baker’s Dictionary of Theology")]), _vm._v(" (E. F. Harrison, ed.; 1960), pp. 189–193; E. C. Colwell, "), _c('em', [_vm._v("An Approach to the Teaching of Jesus")]), _vm._v(" (1947), p. 121; "), _c('em', [_vm._v("Jesus and the Gospel")]), _vm._v(" (1963), pp. 24, 33; C. E. B. Cranfield, "), _c('em', [_vm._v("The Gospel according to Saint Mark")]), _vm._v(" (1959), pp. 63–68; P. E. Davies, “Jesus and the Role of Prophet,” "), _c('em', [_vm._v("JBL")]), _vm._v(", LXIV (1945), pp. 241–254; G. S. Duncan, "), _c('em', [_vm._v("Jesus, Son of Man")]), _vm._v(" (1949), Chaps. 4, 13 (Duncan is rather indecisive about the eschatological element); O. E. Evans, “Kingdom of God,” "), _c('em', [_vm._v("IDB")]), _vm._v(", III, pp. 20–23 (Evans sees a present fulfillment and a future consummation); F. V. Filson, "), _c('em', [_vm._v("Jesus Christ: The Risen Lord")]), _vm._v(" (1956), Chap. 5; R. N. Flew, "), _c('em', [_vm._v("Jesus and His Church")]), _vm._v(" (2nd ed., 1943), Chap. 1; F. J. Foakes-Jackson and K. Lake, "), _c('em', [_vm._v("The Beginnings of Christianity")]), _vm._v(" (1920), I, p. 280; S. M. Gilmour, "), _c('em', [_vm._v("The Gospel Jesus Preached")]), _vm._v(" (1957), Chap. 6; S. E. Johnson, "), _c('em', [_vm._v("Jesus in His Own Times")]), _vm._v(" (1958), p. 122; E. Käsemann. "), _c('em', [_vm._v("ZTK")]), _vm._v(", LI (1954), pp. 150–151; J. Lindblom, “The Idea of the Kingdom of God,” "), _c('em', [_vm._v("ET")]), _vm._v(", LI (1939–40), pp. 91–96; H. K. Luce, “Kingdom of God,” "), _c('em', [_vm._v("HDB")]), _vm._v(" (rev. ed. by F. C. Grant and H. H. Rowley, 1963), p. 554; C. F. D. Moule, "), _c('em', [_vm._v("The Birth of the New Testament")]), _vm._v(" (1962), p. 99; O. Piper, "), _c('em', [_vm._v("God in History")]), _vm._v(" (1939); Herman Ridderbos, “Kingdom of God,” in "), _c('em', [_vm._v("The New Bible Dictionary")]), _vm._v(" (J. D. Douglas, ed.; 1962), pp. 693–697; see also Ridderbos, "), _c('em', [_vm._v("The Coming of the Kingdom")]), _vm._v(" (1962), an important volume which came to hand too late to be used in the present study; H. Roberts, "), _c('em', [_vm._v("Jesus and the Kingdom of God")]), _vm._v(" (1955), Chap. 1; J. M. Robinson, “Jesus’ Understanding of History,” "), _c('em', [_vm._v("JBR")]), _vm._v(", XXIII (1955), pp. 17–24; “The Formal Structure of Jesus’ Message” in "), _c('em', [_vm._v("Current Issues in New Testament Interpretation")]), _vm._v(" (W. Klassen and G. F. Snyder, eds.; 1962), pp. 97 ff.; J. D. Smart, "), _c('em', [_vm._v("The Interpretation of Scripture")]), _vm._v(" (1961), pp. 110 f.; V. Taylor, "), _c('em', [_vm._v("The Life and Ministry of Jesus")]), _vm._v(" (1954), Chap. 14; H. E. W. Turner, "), _c('em', [_vm._v("Jesus Master and Lord")]), _vm._v(" (1953), Chap. 9; R. McL. Wilson in "), _c('em', [_vm._v("Peake’s Commentary on the Bible")]), _vm._v(" (M. Black and H. H. Rowley, eds.; 1962), p. 801; I. W. Batdorf, "), _c('em', [_vm._v("Interpreting the Beatitudes")]), _vm._v(" (1966), pp. 99 ff.; E. Linnemann, "), _c('em', [_vm._v("Jesus of the Parables")]), _vm._v(" (1966), pp. 38 ff., 132 ff.; I. H. Marshall, "), _c('em', [_vm._v("Eschatology and the Parables")]), _vm._v(" (1963); J. D. Kingsbury, "), _c('em', [_vm._v("The Parables of Jesus in Matthew")]), _vm._v(" 13 (1969), pp. 17 ff.; R. P. Berkey, “Realized Eschatology and the Post-Bultmannians,” "), _c('em', [_vm._v("ET")]), _vm._v(", LXXXIV (1972–3); pp. 72–77; John Reumann, "), _c('em', [_vm._v("Jesus in the Church’s Gospels")]), _vm._v(" (1968), pp. 154 ff.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-162",
      "id": "foot_162"
    }
  }, [_vm._v("162")]), _vm._v(". A. N. Wilder, "), _c('em', [_vm._v("Eschatology and Ethics in the Teaching of Jesus")]), _vm._v(" (1950), p. 38. See above, "), _c('a', {
    attrs: {
      "href": "#/#foot-143"
    }
  }, [_vm._v("p. 32, n. 143")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#foot-163",
      "id": "foot_163"
    }
  }, [_vm._v("163")]), _vm._v(". "), _c('em', [_vm._v("Jesus")]), _vm._v(" (1949), p. 70.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-8e2bab3a","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/009_Chapter01.vue
// module id = 81
// module chunks = 0